import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, BarChart3, Check, CheckCircle2, ChevronDown, CirclePlus, Clock3,
  Coins, Copy, GitBranch, Hash, Info, LogOut, Medal, Network, ReceiptText,
  RefreshCw, Rocket, Send, Share2, Sparkles, TrendingUp, Users, Wallet, X
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { formatGwei, formatUnits, parseUnits } from "viem";
import { useAccount, useBlockNumber, useChainId, useConnect, useGasPrice, useReadContract, useSignMessage, useSwitchChain, useWriteContract } from "wagmi";
import { getAccount, getPublicClient, readContract, waitForTransactionReceipt } from "wagmi/actions";
import {
  erc20Abi, marketParticipationAbi, marketParticipationV2Abi, marketPayoutVaultAbi
} from "@ocs/contract-abis";
import { api } from "../api";
import { ensureConfiguredBscNetwork } from "../chain";
import {
  appChain, hasMarketParticipationContracts, hasMarketRewardContracts,
  hasConfiguredContracts, iageAddress, isLocal, marketParticipationAddress,
  sageAddress, treasuryAddress, usdtAddress
} from "../constants";
import type { Assets, ParticipationEligibility } from "../types";
import { useWalletSession } from "../useSession";
import { wagmiConfig } from "../wagmi";
import { copyText } from "../clipboard";
import {
  formatPositionCountdown,
  formatShanghaiTimestamp
} from "../position-countdown";
import {
  FIXED_PARTICIPATION_RAW, formatUsdtBalance, hasParticipationBalance, USDT_DECIMALS
} from "../usdt";
import { synchronizedMaxWithdrawalValue } from "../withdrawal";
import { marketRewardClaimErrorMessage } from "../reward-claim-error";
import { isNodeReferralOwner } from "../referral-identity";
import { assertPrincipalWithdrawalAllowed, isPrincipalWithdrawalBlocked } from "../principal-withdrawal-policy";
import "../sage-v4.css";

type Tab = "home" | "signals" | "hold";
type Language = "zh" | "en";
type ProductCode = "MARKET_7D" | "MARKET_90D" | "MARKET_180D";
// Keep the leaderboard implementation available, but do not expose it in the
// DAPP until operations explicitly reopens the home-page ranking display.
const HOME_LEADERBOARD_VISIBLE = false;
// Keep the partner participation implementation available for a future release.
// This release closes the DAPP entry only; backend node capabilities and legacy
// node identity displays remain active.
const PARTNER_PARTICIPATION_VISIBLE = false;
// Active-position top-ups are temporarily closed in the DApp. The API and
// contracts keep their existing participation actions for future use.
const MARKET_TOP_UP_DISABLED_MESSAGE = "当前有效仓位暂不开放加仓；请在仓位退出确认后重新开仓";
type Product = {
  product_code: ProductCode; product_name: string; lock_days: number;
  minimum_principal: string; min_daily_rate: string; max_daily_rate: string;
  max_iage_ratio: string; daily_position_limit: number | null;
  today_position_count: string; published_daily_rate: string | null; published_rate_date: string | null; remainingToday: number | null;
};
type LeaderItem = { user_address: string; rank_level: number; settlement_date: string; leaderboard_position: number; tie_count: number };
type Leaderboard = { totalAccounts: number; latestBlock: string | null; snapshotDate: string | null; items: LeaderItem[] };
type MarketMe = {
  account: { is_node_identity: number } | null;
  positions: Array<{ position_id: string; contract_position_id: string | null; identity_type: number; product_code: ProductCode; product_name: string; lock_days: number; principal_usdt_equivalent: string; base_principal_usdt: string; usdt_amount: string; market_usdt_amount: string; iage_amount: string; market_iage_amount: string; iage_ratio: string; started_at: string; maturity_at: string; cumulative_static_earning: string; cumulative_dynamic_earning: string; earning_cap_amount: string; position_status: number; remaining_earning_days: number; latest_daily_rate: string | null; latest_rate_date: string | null; normal_exit_available: number; breach_exit_id: string | null; breach_requested_at: string | null; earning_stop_date: string | null }>;
  earnings: Array<{
    earning_type: number; total_earned: string; sage_amount: string; iage_amount: string;
    sage_available_amount: string; iage_available_amount: string;
    available_amount: string; reserved_amount: string;
    available_usdt_gross: string; available_usdt_fee: string; available_usdt_net: string;
    sage_reference_price: string;
  }>;
  withdrawals: Array<{ withdraw_id: string; requested_amount: string; net_amount: string; withdrawal_status: number; requested_at: string; withdrawal_type?: number; wallet_usdt_net_amount?: string | null; iage_principal_written_off?: string }>;
  iagePrincipalExitPolicy?: {
    mode: "WRITE_OFF"; active: boolean | null; chainId: number;
    contractAddress: string; effectiveFromBlock: string; policyVersion: string;
  } | null;
  referral: { inviter_address: string | null; direct_count: string | number };
  rank: { rank_level: number; reward_rate: string; small_area_performance: string; total_team_performance?: string } | null;
  teamRewards: Array<{ settlement_date: string; reward_type: number; credited_reward: string }>;
  teamRewardTotals: { normal_team_total: string; peer_total: string };
  directRewards: Array<{ settlement_date: string; reward_count: string | number; credited_reward: string }>;
  staticRewards: Array<{ position_id: string; settlement_date: string; daily_rate: string; credited_reward: string }>;
  iageTransfers: Array<{
    transfer_id: string; sender_address: string; recipient_address: string;
    iage_amount: string; direction: "SENT" | "RECEIVED"; created_at: string;
  }>;
  pendingClaims: PendingRewardClaim[];
  sageReferencePrice: string;
};
type PendingRewardClaim = {
  claim_id: `0x${string}`;
  reward_type: number;
  reward_type_name: RewardTypeName;
  claim_status: number;
  vault_id: number;
  vault_address: `0x${string}`;
  settlement_vault_address: `0x${string}` | "";
  sage_gross: string;
  iage_gross: string;
  expected_usdt_gross: string;
  expected_usdt_fee: string;
  expected_usdt_net: string;
  deadline: string;
  reissue_count: number;
  prepared_at: string;
  reissued_at: string | null;
  submitted_at: string | null;
  tx_hash: `0x${string}` | null;
  can_retry: boolean;
};
type ReferralMe = { relation: { inviter_address: string; referral_code: string; bound_at?: string } | null; code: { referral_code: string; code_status: number; invitation_type: "NODE" | "ORDINARY" } | null; stats: { direct_count: string | number; active_direct_count: string | number; active_node_direct_count: string | number; active_ordinary_direct_count: string | number } };
type ReferralNetworkNode = {
  inviterAddress: string;
  address: string;
  boundAt: string;
  depth: number;
  isNodeIdentity: boolean;
  isActive: boolean;
  hasActivePosition: boolean;
  position: null | {
    positionId: string;
    status: number;
    principal: string;
    productCode: ProductCode;
    lockDays: number;
    startedAt: string;
    maturityAt: string;
    firstEarningDate: string;
    lastEarningDate: string;
  };
  rank: { level: number; totalTeamPerformance: string; largestAreaPerformance: string; smallAreaPerformance: string };
};
type ReferralNetwork = {
  rootAddress: string;
  rootPerformance?: { totalTeamPerformance: string; largestAreaPerformance: string; smallAreaPerformance: string };
  directCount: number;
  activeDirectCount: number;
  teamCount: number;
  activeTeamCount: number;
  maxDepth: number;
  truncated: boolean;
  nodes: ReferralNetworkNode[];
};
type ResolvedReferral = { valid: boolean; referralCode: string; inviter: string; invitationType: "NODE" | "ORDINARY"; allowedChoices: Array<"NODE" | "ORDINARY"> };
type NodeReferral = { bound: boolean; inviter?: string; inviterAddress?: string; referralCode?: string };
type NodePrincipalExit = {
  withdrawId: string; status: number; amount: string; requestedAmount: string;
  penaltyAmount: string; feeAmount: string; cumulativeEarningWithdrawn: string;
  earlyExit: boolean; maturityAt: string;
};
type NodeDirectClaim = {
  withdrawId: string; status: number;
  amounts: { gross: string; fee: string; net: string };
};
type NodeWithdrawal = {
  withdraw_id: string; withdrawal_type: number; requested_amount: string;
  fee_amount: string; net_amount: string; penalty_amount: string;
  withdrawal_status: number; request_time: string; submitted_at: string | null;
  confirmed_at: string | null; tx_hash: string | null; failure_reason: string | null;
  confirmation_count: number; flow_status: number | null; block_number: string | null;
};
type Quote = { product: Product; quote: { principal: string; usdtAmount: string; iageAmount: string; projectedMinStatic: string; projectedMaxStatic: string; earningCap: string } };
const ZERO_INTERNAL_IAGE_CREDIT_ID = `0x${"0".repeat(64)}` as const;
const USDT_ONLY_QUOTE_MESSAGE = "参与报价已变化，请重新核验 100% USDT 开仓规则";
function isUsdtOnlyParticipationQuote(value: Quote | undefined, productCode: ProductCode, principal: string): value is Quote {
  try {
    if (!value?.quote || !/^0+(?:\.0+)?$/.test(value.quote.iageAmount)) return false;
    const expectedUsdt = parseUnits(principal, 18);
    return expectedUsdt > 0n
      && parseUnits(value.quote.usdtAmount, 18) === expectedUsdt
      && (value.quote.principal == null || parseUnits(value.quote.principal, 18) === expectedUsdt)
      && (value.product == null || value.product.product_code === productCode);
  } catch {
    return false;
  }
}
type ParticipationAuthorization = {
  action: "OPEN" | "INCREASE" | "LINK_NODE";
  inviter: `0x${string}`;
  nonce: string;
  deadline: string;
  contractPositionId: string | null;
  sourceNodePositionId: string | null;
  maturesAt: string | null;
  authorization: `0x${string}`;
  contractAddress: `0x${string}`;
  contractVersion: 1 | 2;
  internalIageCreditId: `0x${string}`;
  // Authorization responses use raw 18-decimal token units.
  usdtAmount?: string;
  iageAmount?: string;
};
function assertOpenParticipation(action: ParticipationAuthorization["action"]) {
  if (action !== "OPEN") throw new Error(MARKET_TOP_UP_DISABLED_MESSAGE);
}
type MarketBreachExit = {
  breach_exit_id: string;
  position_id: string;
  contract_position_id: string;
  requested_at: string;
  earning_stop_date: string;
  irreversible: true;
  principalSettlement: "MANUAL";
  previouslySettledEarningsRemainClaimable: true;
};
type RewardTypeName = "STATIC" | "DYNAMIC";
type RewardClaim = {
  claimId: `0x${string}`; batchId: `0x${string}`; vaultId: number;
  vaultAddress: `0x${string}`; recipient: `0x${string}`; rewardType: number;
  rewardTypeName: RewardTypeName; sageGross: string; iageGross: string;
  deadline: string; authorization: `0x${string}`;
  settlementAsset: "USDT";
  minimumSageUsdtOut: string; sagePrice: string; sageQuoteDeadline: string | null;
  sageQuoteAuthorization: `0x${string}`;
  expectedUsdtGross: string; expectedUsdtFee: string; expectedUsdtNet: string;
  reissued: boolean;
};
type IageTransferPrepared = {
  sender: string; recipient: string; amount: string; requestId: string;
  deadline: number; message: string;
};
type IageTransferResult = {
  transferId: string; sender: string; recipient: string; iageAmount: string;
  createdAt: string; idempotent: boolean;
};
type AssetConfig = {
  enabled: boolean; sageQuotePrice: string; iageExchangeRate: string;
  iageUsdtRedemptionEnabled: false; sagePriceBandPercent: number;
  sageGrowthStartedAt: number | null; sageDailyGrowthPermille: number;
  participationV2Address: `0x${string}` | null;
  internalIageParticipationEnabled: boolean;
};
type Signal = { sym: string; mark: string; name: string; price: number; delta: number; share: number; fair: number; index: number; score: number };
type SectorSignals = {
  enabled: true;
  source: "TWELVE_DATA" | "TWELVE_DATA_STALE";
  provider: "Twelve Data";
  fetchedAt: string;
  marketOpen: boolean;
  stale: boolean;
  items: Array<{
    sym: string; mark: string; nameZh: string; nameEn: string;
    proxySymbol: string; proxyName: string; price: number; delta: number;
    share: number; fair: number; index: number; score: number;
    volume: string; averageVolume: string; marketOpen: boolean; quoteTimestamp: string;
  }>;
};
type SageInterval = "1m" | "5m" | "15m" | "4h" | "1d" | "all";
const SAGE_INTERVALS = ["1m", "5m", "15m", "4h", "1d", "all"] as const;
const SAGE_INTERVAL_LABELS: Record<SageInterval, { zh: string; en: string }> = {
  "1m": { zh: "1分钟", en: "1 min" },
  "5m": { zh: "5分钟", en: "5 min" },
  "15m": { zh: "15分钟", en: "15 min" },
  "4h": { zh: "4小时", en: "4 hour" },
  "1d": { zh: "日线", en: "Daily" },
  "all": { zh: "ALL", en: "ALL" }
};
type SageCandle = { time: string; open: string; high: string; low: string; close: string; volume: string; trades: number };
type SageCandles = {
  symbol: "SAGE/USDT"; interval: SageInterval;
  source: "INTERNAL_MODEL"; currentPrice: string;
  referencePrice: string; bandPercent: number; dailyGrowthPermille: number;
  candles: SageCandle[];
};

const short = (address: string) => `${address.slice(0, 6)}…${address.slice(-4)}`;
const maskedReferralAddress = (address: string) => `${address.slice(0, 6)}…${address.slice(-4)}`;
const money = (value: string | number | undefined, digits = 2) => Number(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const tokenAmount = (value: string | undefined) => {
  const [whole = "0", fraction = ""] = String(value ?? "0").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const trimmed = fraction.replace(/0+$/, "");
  return trimmed ? `${grouped}.${trimmed}` : grouped;
};
const fixed18Units = (value: string | undefined) => {
  try { return parseUnits(value || "0", 18); } catch { return 0n; }
};
const sumFixed18 = (...values: Array<string | undefined>) => formatUnits(
  values.reduce((sum, value) => sum + fixed18Units(value), 0n),
  18
);
const parseFixed18Input = (value: string) => {
  if (!/^\d+(?:\.\d{0,18})?$/.test(value)) return null;
  try { return parseUnits(value.endsWith(".") ? `${value}0` : value, 18); } catch { return null; }
};
const pct = (value: string | number) => `${(Number(value) * 100).toFixed(2)}%`;
const hue = (address: string) => Number.parseInt(address.slice(-5), 16) % 360;
const statusText = (value: number) => value === 4 ? "已完成" : value === 5 ? "已失败" : "处理中";
const textFor = (language: Language, zh: string, en: string) => language === "zh" ? zh : en;
type ContractGasRequest = {
  account: `0x${string}`;
  address: `0x${string}`;
  abi: readonly unknown[];
  functionName: string;
  args?: readonly unknown[];
  value?: bigint;
};
const bufferedContractGas = async (request: ContractGasRequest) => {
  const publicClient = getPublicClient(wagmiConfig, { chainId: appChain.id });
  if (!publicClient) throw new Error("无法连接链上节点，请稍后重试");
  const estimate = await publicClient.estimateContractGas(request as never);
  return estimate + estimate / 4n + 10_000n;
};
const friendlyWalletError = (message: string | undefined) => {
  if (!message) return undefined;
  // Closing or rejecting a TokenPocket confirmation is a normal user action.
  // viem appends request/contract internals to this error, which must never be
  // rendered as a red RPC failure in the product UI.
  if (/user rejected|rejected the request|request rejected|action_rejected|code[: ]+4001|用户拒绝/i.test(message)) {
    return undefined;
  }
  if (message.includes("gas but got 21000")
      || message.includes("eth_getTransactionByHash")
      || message.includes("Invalid parameters were provided to the RPC method")) {
    return "TokenPocket 本地链交易参数异常，请刷新钱包后重试。";
  }
  return message;
};

export function SageV4App() {
  const wallet = useAccount();
  const { connectors, connect, isPending: connecting } = useConnect();
  const session = useWalletSession();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const { signMessageAsync } = useSignMessage();
  const queryClient = useQueryClient();
  const rpcBlockNumber = useBlockNumber({
    chainId: appChain.id,
    watch: true,
    query: { refetchInterval: 3_000 }
  });
  const rpcGasPrice = useGasPrice({
    chainId: appChain.id,
    query: { refetchInterval: 3_000 }
  });
  const [tab, setTab] = useState<Tab>("home");
  const [language, setLanguage] = useState<Language>("zh");
  const [walletSheet, setWalletSheet] = useState(false);
  const [rows, setRows] = useState(23);
  const [sort, setSort] = useState<"share" | "fair" | "index" | "score">("score");
  const [direction, setDirection] = useState(-1);
  const [signal, setSignal] = useState("SAGE");
  const [sageDetail, setSageDetail] = useState(false);
  const [sageInterval, setSageInterval] = useState<SageInterval>("15m");
  const [selectedCode, setSelectedCode] = useState<ProductCode>("MARKET_180D");
  const [principal, setPrincipal] = useState("1000");
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const copyResetTimer = useRef<number | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawRewardType, setWithdrawRewardType] = useState<RewardTypeName>("STATIC");
  const [historyTab, setHistoryTab] = useState<"all" | "in" | "out">("all");
  const [exitConfirmId, setExitConfirmId] = useState<string | null>(null);
  const [nodeDepositHash, setNodeDepositHash] = useState<`0x${string}` | null>(null);
  const principalWithdrawalBlocked = isPrincipalWithdrawalBlocked(wallet.address, session.account.address);

  useEffect(() => {
    setExitConfirmId(null);
  }, [wallet.address]);

  useEffect(() => {
    if (session.authenticated) setWalletSheet(false);
  }, [session.authenticated]);

  useEffect(() => () => {
    if (copyResetTimer.current != null) window.clearTimeout(copyResetTimer.current);
  }, []);

  const leaderboard = useQuery({
    queryKey: ["market-leaderboard"],
    queryFn: () => api<Leaderboard>("/market/leaderboard?limit=100"),
    enabled: HOME_LEADERBOARD_VISIBLE,
    refetchInterval: HOME_LEADERBOARD_VISIBLE ? 30_000 : false
  });
  const products = useQuery({ queryKey: ["market-products"], queryFn: () => api<Product[]>("/market/products"), refetchInterval: 15_000 });
  const market = useQuery({ queryKey: ["market-me", session.account.address], queryFn: () => api<MarketMe>("/market/me"), enabled: session.authenticated, refetchInterval: 10_000 });
  const node = useQuery({ queryKey: ["assets", session.account.address], queryFn: () => api<Assets>("/account/assets"), enabled: session.authenticated, refetchInterval: 10_000 });
  const nodeWithdrawals = useQuery({
    queryKey: ["node-withdrawals", session.account.address],
    queryFn: () => api<NodeWithdrawal[]>("/withdrawals"),
    enabled: session.authenticated,
    refetchInterval: 10_000
  });
  const nodeEligibility = useQuery({ queryKey: ["participation-eligibility", session.account.address], queryFn: () => api<ParticipationEligibility>("/account/participation-eligibility"), enabled: session.authenticated, refetchInterval: 5_000 });
  const nodeReferral = useQuery({ queryKey: ["node-referral", session.account.address], queryFn: () => api<NodeReferral>("/referrals/my-referral"), enabled: session.authenticated, refetchInterval: 5_000 });
  const referral = useQuery({ queryKey: ["market-referral-me", session.account.address], queryFn: () => api<ReferralMe>("/market/referrals/me"), enabled: session.authenticated, refetchInterval: 5_000 });
  const referralNetwork = useQuery({
    queryKey: ["market-referral-network", session.account.address],
    queryFn: () => api<ReferralNetwork>("/market/referrals/network"),
    enabled: session.authenticated && tab === "home",
    refetchInterval: 15_000
  });
  const config = useQuery({ queryKey: ["market-asset-config"], queryFn: () => api<AssetConfig>("/market/assets"), refetchInterval: 30_000 });
  const sectorSignals = useQuery({
    queryKey: ["market-sector-signals"],
    queryFn: () => api<SectorSignals>("/market/signals/sectors"),
    refetchInterval: 60_000,
    retry: 1
  });
  const sageCandles = useQuery({
    queryKey: ["sage-candles", sageInterval],
    queryFn: () => api<SageCandles>(`/market/assets/sage-candles?interval=${sageInterval}&limit=48`),
    refetchInterval: 30_000
  });
  const sageSignalView = useMemo(
    () => sageCandles.data ?? {
      symbol: "SAGE/USDT" as const,
      interval: sageInterval,
      source: "INTERNAL_MODEL" as const,
      currentPrice: config.data?.sageQuotePrice ?? "0",
      referencePrice: config.data?.sageQuotePrice ?? "0",
      bandPercent: config.data?.sagePriceBandPercent ?? 15,
      dailyGrowthPermille: config.data?.sageDailyGrowthPermille ?? 0,
      candles: []
    },
    [config.data, sageCandles.data, sageInterval]
  );
  const nodeUsdtBalance = useReadContract({
    address: usdtAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: wallet.address ? [wallet.address] : undefined,
    chainId: appChain.id,
    query: { enabled: Boolean(wallet.address && hasConfiguredContracts), refetchInterval: 5_000 }
  });
  const nodeUsdtDecimals = useReadContract({
    address: usdtAddress,
    abi: erc20Abi,
    functionName: "decimals",
    chainId: appChain.id,
    query: { enabled: hasConfiguredContracts, staleTime: Number.POSITIVE_INFINITY }
  });
  const walletSageBalance = useReadContract({
    address: sageAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: wallet.address ? [wallet.address] : undefined,
    chainId: appChain.id,
    query: { enabled: Boolean(wallet.address && sageAddress), refetchInterval: 5_000 }
  });
  const syncTestAssets = useMutation({
    mutationFn: async () => {
      if (!isLocal || !wallet.connector) throw new Error("仅本地测试环境支持同步测试资产");
      const provider = await wallet.connector.getProvider() as {
        request: (args: { method: string; params?: unknown }) => Promise<unknown>;
      };
      const assets = [
        { address: usdtAddress, symbol: "lUSDT" },
        { address: sageAddress, symbol: "lSAGE" }
      ];
      for (const asset of assets) {
        const accepted = await provider.request({
          method: "wallet_watchAsset",
          params: { type: "ERC20", options: { ...asset, decimals: 18 } }
        });
        if (accepted === false) throw new Error(`${asset.symbol} 未在 TokenPocket 中确认`);
      }
      await Promise.all([nodeUsdtBalance.refetch(), walletSageBalance.refetch()]);
    }
  });

  const pendingReferral = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("ref") || params.get("mref")
      || localStorage.getItem("ocs:referral-code")
      || localStorage.getItem("ocs:market-referral-code");
  }, []);
  useEffect(() => { if (pendingReferral) localStorage.setItem("ocs:referral-code", pendingReferral); }, [pendingReferral]);
  const resolved = useQuery({ queryKey: ["platform-referral-resolve", pendingReferral], queryFn: () => api<ResolvedReferral>(`/market/referrals/resolve?ref=${pendingReferral}`), enabled: Boolean(pendingReferral && !referral.data?.relation) });

  const nodeDepositStorageKey = wallet.address
    ? `ocs:pending-deposit:${wallet.address.toLowerCase()}`
    : null;
  useEffect(() => {
    if (!nodeDepositStorageKey) {
      setNodeDepositHash(null);
      return;
    }
    setNodeDepositHash(localStorage.getItem(nodeDepositStorageKey) as `0x${string}` | null);
  }, [nodeDepositStorageKey]);
  useEffect(() => {
    if (!nodeDepositHash || !nodeDepositStorageKey) return;
    void waitForTransactionReceipt(wagmiConfig, { hash: nodeDepositHash, confirmations: 3 })
      .then(async () => {
        localStorage.removeItem(nodeDepositStorageKey);
        setNodeDepositHash(null);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["assets"] }),
          queryClient.invalidateQueries({ queryKey: ["participation-eligibility"] }),
          queryClient.invalidateQueries({ queryKey: ["node-referral"] }),
          queryClient.invalidateQueries({ queryKey: ["market-referral-me"] }),
          queryClient.invalidateQueries({ queryKey: ["market-me"] })
        ]);
      })
      .catch(async () => {
        localStorage.removeItem(nodeDepositStorageKey);
        setNodeDepositHash(null);
        await queryClient.invalidateQueries({ queryKey: ["participation-eligibility"] });
      });
  }, [nodeDepositHash, nodeDepositStorageKey, queryClient]);

  const bindReferral = useMutation({
    mutationFn: async () => {
      if (!pendingReferral) throw new Error("没有待绑定的邀请码");
      if (resolved.data?.invitationType === "NODE") {
        const { nonce } = await api<{ nonce: string }>("/referrals/bind-nonce");
        return api("/referrals/bind", {
          method: "POST",
          body: JSON.stringify({
            referralCode: pendingReferral,
            bindRequestId: crypto.randomUUID(),
            nonce,
            source: 1
          })
        });
      }
      const { nonce } = await api<{ nonce: string }>("/market/referrals/bind-nonce");
      return api("/market/referrals/bind", {
        method: "POST",
        body: JSON.stringify({ referralCode: pendingReferral, nonce })
      });
    },
    onSuccess: async () => {
      localStorage.removeItem("ocs:referral-code");
      localStorage.removeItem("ocs:market-referral-code");
      await Promise.all([nodeReferral.refetch(), referral.refetch(), referralNetwork.refetch()]);
    }
  });
  const participateAsNode = useMutation({
    mutationFn: async () => {
      if (!session.authenticated || !wallet.address) throw new Error("请先完成钱包签名登录");
      if (!hasConfiguredContracts) throw new Error("合伙人参与合约地址尚未配置");
      const currentNodeReferral = await api<NodeReferral>("/referrals/my-referral");
      if (!currentNodeReferral.bound) {
        throw new Error("请先在邀请区域确认绑定合伙人推荐人");
      }
      const eligibility = await api<ParticipationEligibility>("/account/participation-eligibility");
      if (!eligibility.eligible) {
        throw new Error(eligibility.reason === "ORDINARY_IDENTITY_ALREADY_SELECTED"
          ? "首次参与已选择普通参与，不能再申请合伙人"
          : eligibility.depositPending
            ? "已有一笔合伙人参与交易等待确认，请勿重复提交"
            : "当前账户已有有效合伙人身份");
      }
      await ensureConfiguredBscNetwork(wallet.connector, chainId, switchChainAsync);
      const decimals = await nodeUsdtDecimals.refetch();
      if (decimals.data !== USDT_DECIMALS) throw new Error(`USDT 精度校验失败：需要 ${USDT_DECIMALS} 位精度`);
      const balance = await nodeUsdtBalance.refetch();
      if (!hasParticipationBalance(balance.data)) throw new Error("钱包 USDT 余额不足 1,000");
      await api("/account/participation-choice", {
        method: "POST",
        body: JSON.stringify({ choice: "NODE" })
      });
      const expectedAccount = wallet.address;
      const transferRequest = {
        account: expectedAccount,
        address: usdtAddress,
        abi: erc20Abi,
        functionName: "transfer",
        args: [treasuryAddress, FIXED_PARTICIPATION_RAW]
      } as const;
      const hash = await writeContractAsync({
        ...transferRequest,
        gas: await bufferedContractGas(transferRequest),
        chainId: appChain.id
      });
      localStorage.setItem(`ocs:pending-deposit:${expectedAccount.toLowerCase()}`, hash);
      setNodeDepositHash(hash);
      return hash;
    },
    onSuccess: async () => {
      await Promise.all([nodeReferral.refetch(), nodeEligibility.refetch(), nodeUsdtBalance.refetch()]);
    }
  });
  const assertPrincipalWithdrawalAccess = () => {
    const currentAddress = getAccount(wagmiConfig).address;
    assertPrincipalWithdrawalAllowed(wallet.address, session.account.address, currentAddress);
    if (!session.authenticated || !currentAddress
        || wallet.address?.toLowerCase() !== currentAddress.toLowerCase()
        || session.account.address?.toLowerCase() !== currentAddress.toLowerCase()) {
      throw new Error("请先完成钱包签名登录");
    }
  };
  const exitNodePrincipal = useMutation({
    mutationKey: ["node-principal-exit", wallet.address],
    mutationFn: async () => {
      assertPrincipalWithdrawalAccess();
      return api<NodePrincipalExit>("/withdrawals/principal", {
        method: "POST",
        body: JSON.stringify({ idempotencyKey: crypto.randomUUID() })
      });
    },
    onSuccess: async () => { await node.refetch(); }
  });
  const claimNodeDirect = useMutation({
    mutationFn: (amount: string) => api<NodeDirectClaim>("/withdrawals", {
      method: "POST",
      body: JSON.stringify({ type: 1, amount, idempotencyKey: crypto.randomUUID() })
    }),
    onSuccess: async () => {
      await Promise.all([node.refetch(), nodeWithdrawals.refetch()]);
    }
  });

  const selectedProduct = products.data?.find((item) => item.product_code === selectedCode);
  const currentMarketPosition = market.data?.positions.find((position) => [1, 2, 6].includes(position.position_status));
  useEffect(() => {
    if (currentMarketPosition && selectedCode !== currentMarketPosition.product_code) {
      setSelectedCode(currentMarketPosition.product_code);
    }
  }, [currentMarketPosition?.position_id, currentMarketPosition?.position_status,
    currentMarketPosition?.identity_type, currentMarketPosition?.product_code, selectedCode]);
  useEffect(() => {
    const consumesNewCapacity = !currentMarketPosition;
    if (consumesNewCapacity && selectedCode === "MARKET_7D" && selectedProduct?.remainingToday === 0) {
      setSelectedCode("MARKET_90D");
    }
  }, [currentMarketPosition?.position_id, currentMarketPosition?.position_status,
    selectedCode, selectedProduct?.remainingToday]);
  const quote = useMutation({
    mutationKey: ["market-quote", wallet.address],
    mutationFn: () => {
      if (currentMarketPosition?.position_status === 1) throw new Error(MARKET_TOP_UP_DISABLED_MESSAGE);
      return api<Quote>("/market/quote", { method: "POST", body: JSON.stringify({ productCode: selectedCode, principal, iageRatio: "0" }) }).then((value) => {
        if (!isUsdtOnlyParticipationQuote(value, selectedCode, principal)) throw new Error(USDT_ONLY_QUOTE_MESSAGE);
        return value;
      });
    }
  });
  useEffect(() => { quote.reset(); }, [selectedCode, principal, wallet.address]);
  const participate = useMutation({
    mutationKey: ["market-participate", wallet.address],
    mutationFn: async () => {
      if (currentMarketPosition?.position_status === 1) throw new Error(MARKET_TOP_UP_DISABLED_MESSAGE);
      if (!session.authenticated || !wallet.address) throw new Error("请先完成钱包签名登录");
      if (!referral.data?.relation) throw new Error("请先确认绑定推荐人");
      if (!marketParticipationAddress || !hasMarketParticipationContracts) throw new Error("Market 主网合约尚未配置");
      // Recheck immediately before requesting authorization: a stale page must
      // not submit an active-position top-up.
      const latestMarket = await api<MarketMe>("/market/me");
      if (latestMarket.positions.some((position) => position.position_status === 1)) {
        throw new Error(MARKET_TOP_UP_DISABLED_MESSAGE);
      }
      // Always obtain a fresh USDT-only quote. A cached quote from an older
      // page or product selection must never reserve or consume internal IAGE.
      const currentQuote = await api<Quote>("/market/quote", { method: "POST", body: JSON.stringify({ productCode: selectedCode, principal, iageRatio: "0" }) });
      if (!isUsdtOnlyParticipationQuote(currentQuote, selectedCode, principal)) throw new Error(USDT_ONLY_QUOTE_MESSAGE);
      await ensureConfiguredBscNetwork(wallet.connector, chainId, switchChainAsync);
      const usdtAmount = parseUnits(currentQuote.quote.usdtAmount, 18);
      const iageAmount = 0n;
      const assetConfig = config.data ?? await api<AssetConfig>("/market/assets");
      const currentContractPositionId = currentMarketPosition?.contract_position_id
        ? BigInt(currentMarketPosition.contract_position_id) : null;
      const usesV2 = currentContractPositionId == null
        || currentContractPositionId >= 1_000_000_000_000n;
      const targetContract = usesV2
        ? assetConfig.participationV2Address : marketParticipationAddress;
      if (!targetContract) throw new Error("Market 参与合约尚未配置");
      const activationNonce = await readContract(wagmiConfig, {
        address: targetContract,
        abi: marketParticipationAbi,
        functionName: "activationNonces",
        args: [wallet.address],
        chainId: appChain.id
      });
      const requestId = crypto.randomUUID();
      const activation = await api<ParticipationAuthorization>("/market/participation-authorization", {
        method: "POST",
        body: JSON.stringify({
          productCode: selectedCode,
          usdtAmount: currentQuote.quote.usdtAmount,
          iageAmount: "0",
          nonce: activationNonce.toString(),
          requestId
        })
      });
      // The server may know about an active position before the DApp refreshes.
      // Reject stale INCREASE/LINK_NODE authorizations before any token approval.
      assertOpenParticipation(activation.action);
      if (!/^0x0{64}$/i.test(activation.internalIageCreditId)
        || (activation.iageAmount != null && !/^0+$/.test(activation.iageAmount))) {
        throw new Error("开仓授权包含内部 IAGE 权益，请刷新后重新核验 100% USDT 开仓规则");
      }
      if (activation.usdtAmount != null
        && (!/^\d+$/.test(activation.usdtAmount) || BigInt(activation.usdtAmount) !== usdtAmount)) {
        throw new Error(USDT_ONLY_QUOTE_MESSAGE);
      }
      if (activation.contractAddress.toLowerCase() !== targetContract.toLowerCase()) {
        throw new Error("参与合约状态已变化，请刷新后重试");
      }
      if (usdtAmount > 0n) {
        const balance = await readContract(wagmiConfig, {
          address: usdtAddress, abi: erc20Abi, functionName: "balanceOf",
          args: [wallet.address], chainId: appChain.id
        });
        if (balance < usdtAmount) throw new Error(`钱包 USDT 余额不足 ${currentQuote.quote.usdtAmount}`);
        const allowance = await readContract(wagmiConfig, {
          address: usdtAddress, abi: erc20Abi, functionName: "allowance",
          args: [wallet.address, activation.contractAddress], chainId: appChain.id
        });
        if (allowance < usdtAmount) {
          const approvalRequest = {
            account: wallet.address, address: usdtAddress, abi: erc20Abi,
            functionName: "approve", args: [activation.contractAddress, usdtAmount]
          } as const;
          const approval = await writeContractAsync({
            ...approvalRequest, gas: await bufferedContractGas(approvalRequest),
            chainId: appChain.id
          });
          await waitForTransactionReceipt(wagmiConfig, { hash: approval, confirmations: 1 });
        }
      }
      const productId = ({ MARKET_7D: 1, MARKET_90D: 2, MARKET_180D: 3 } as const)[selectedCode];
      const deadline = BigInt(activation.deadline);
      const creditId = ZERO_INTERNAL_IAGE_CREDIT_ID;
      const hash = activation.contractVersion === 2
        ? await (async () => {
          const call = activation.action === "INCREASE"
            ? { functionName: "increasePosition" as const,
              args: [BigInt(activation.contractPositionId!), usdtAmount, iageAmount,
                creditId, deadline, activation.authorization] as const }
            : activation.action === "LINK_NODE"
              ? { functionName: "linkNodePosition" as const,
                args: [BigInt(activation.sourceNodePositionId!), BigInt(activation.maturesAt!),
                  usdtAmount, iageAmount, creditId, activation.inviter,
                  deadline, activation.authorization] as const }
              : { functionName: "openPosition" as const,
                  args: [productId, usdtAmount, iageAmount, creditId,
                    activation.inviter, deadline, activation.authorization] as const };
          const request = {
            account: wallet.address!, address: activation.contractAddress,
            abi: marketParticipationV2Abi, ...call
          } as Parameters<typeof writeContractAsync>[0];
          return writeContractAsync({
            ...request, gas: await bufferedContractGas(request as ContractGasRequest),
            chainId: appChain.id
          } as Parameters<typeof writeContractAsync>[0]);
        })()
        : await (async () => {
          const call = activation.action === "INCREASE"
            ? { functionName: "increasePosition" as const,
              args: [BigInt(activation.contractPositionId!), usdtAmount, 0n,
                deadline, activation.authorization] as const }
            : { functionName: "openPosition" as const,
                args: [productId, usdtAmount, 0n, activation.inviter,
                  deadline, activation.authorization] as const };
          const request = {
            account: wallet.address!, address: activation.contractAddress,
            abi: marketParticipationAbi, ...call
          } as Parameters<typeof writeContractAsync>[0];
          return writeContractAsync({
            ...request, gas: await bufferedContractGas(request as ContractGasRequest),
            chainId: appChain.id
          } as Parameters<typeof writeContractAsync>[0]);
        })();
      await waitForTransactionReceipt(wagmiConfig, { hash, confirmations: 1 });
      return hash;
    },
    onSuccess: async () => { await Promise.all([market.refetch(), products.refetch(), referral.refetch()]); }
  });
  const exitPosition = useMutation({
    mutationKey: ["market-principal-exit", wallet.address],
    mutationFn: async ({ positionId, contractPositionId, penaltyBps }: {
      positionId: string; contractPositionId: string; penaltyBps: number;
    }) => {
      if (!session.authenticated || !wallet.address) throw new Error("请先完成钱包签名登录");
      assertPrincipalWithdrawalAccess();
      if (penaltyBps > 0) {
        return api<MarketBreachExit>(`/market/positions/${positionId}/breach-exit`, {
          method: "POST",
          body: JSON.stringify({
            requestId: crypto.randomUUID(),
            acknowledgedIrreversible: true
          })
        });
      }
      const contractPositionKey = BigInt(contractPositionId);
      const usesV2 = contractPositionKey >= 1_000_000_000_000n;
      const contractAddress = usesV2
        ? config.data?.participationV2Address ?? undefined : marketParticipationAddress;
      if (!contractAddress || !hasMarketParticipationContracts) {
        throw new Error("Market 参与合约尚未配置");
      }
      await ensureConfiguredBscNetwork(wallet.connector, chainId, switchChainAsync);
      assertPrincipalWithdrawalAccess();
      const sharedRequest = {
        account: wallet.address,
        address: contractAddress,
        abi: usesV2 ? marketParticipationV2Abi : marketParticipationAbi,
        args: [contractPositionKey, BigInt(penaltyBps)]
      } as const;
      const request = { ...sharedRequest, functionName: "exitPosition" } as const;
      const gas = await bufferedContractGas(request);
      // Network switching / gas estimation may outlive the connected account.
      // Recheck immediately before opening the wallet transaction prompt.
      assertPrincipalWithdrawalAccess();
      const hash = await writeContractAsync({
        ...request, gas, chainId: appChain.id
      });
      await waitForTransactionReceipt(wagmiConfig, { hash, confirmations: 1 });
      return hash;
    },
    onSuccess: async () => {
      setExitConfirmId(null);
      await market.refetch();
    }
  });
  const claim = useMutation({
    mutationKey: ["market-reward-claim", wallet.address],
    mutationFn: async ({ rewardType, amount }: { rewardType: RewardTypeName; amount?: string }) => {
      if (!wallet.address || !hasMarketRewardContracts) throw new Error("十个收益兑付 Vault 尚未启用");
      await ensureConfiguredBscNetwork(wallet.connector, chainId, switchChainAsync);
      const payload = await api<RewardClaim>("/market/rewards/claim", {
        method: "POST",
        // Capture each submission's ledger and amount before opening the wallet.
        // Omitting amount retains the backend's same-Claim retry behavior.
        body: JSON.stringify({ rewardType, amount })
      });
      const claimRequest = {
        account: wallet.address, address: payload.vaultAddress,
        abi: marketPayoutVaultAbi, functionName: "executeClaimAsUsdt",
        args: [{
          claimId: payload.claimId,
          batchId: payload.batchId,
          recipient: payload.recipient,
          rewardType: payload.rewardType,
          sageGross: parseUnits(payload.sageGross, 18),
          iageGross: parseUnits(payload.iageGross, 18),
          minimumSageUsdtOut: parseUnits(payload.minimumSageUsdtOut, 18),
          sagePrice: parseUnits(payload.sagePrice, 18),
          quoteDeadline: BigInt(payload.sageQuoteDeadline ?? payload.deadline),
          deadline: BigInt(payload.deadline)
        }, payload.authorization, payload.sageQuoteAuthorization]
      } as const;
      const hash = await writeContractAsync({ ...claimRequest,
        gas: await bufferedContractGas(claimRequest), chainId: appChain.id });
      await api(`/market/rewards/claim/${payload.claimId}/submitted`, { method: "POST", body: JSON.stringify({ txHash: hash }) });
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash, confirmations: 1 });
      if (receipt.status !== "success") throw new Error("收益领取交易未成功，请查看交易状态后重试");
      return {
        hash,
        settlementAsset: payload.settlementAsset,
        sageGross: payload.sageGross,
        iageGross: payload.iageGross,
        sagePrice: payload.sagePrice,
        expectedUsdtGross: payload.expectedUsdtGross,
        expectedUsdtFee: payload.expectedUsdtFee,
        expectedUsdtNet: payload.expectedUsdtNet
      };
    },
    onSuccess: async () => { setWithdrawAmount(""); await market.refetch(); }
  });
  const selectWithdrawalLedger = (rewardType: RewardTypeName) => {
    // Resetting an in-flight mutation would re-enable submission while the
    // previous wallet request is still signing or awaiting confirmation.
    if (claim.isPending) return;
    setWithdrawRewardType(rewardType);
    setWithdrawAmount("");
    claim.reset();
  };
  const transferIage = useMutation({
    mutationFn: async ({ recipient, amount }: { recipient: string; amount: string }) => {
      if (!session.authenticated || !wallet.address) {
        throw new Error("请先完成钱包签名登录");
      }
      const requestId = crypto.randomUUID();
      const prepared = await api<IageTransferPrepared>("/market/rewards/iage-transfer/prepare", {
        method: "POST",
        body: JSON.stringify({ recipient, amount, requestId })
      });
      const signature = await signMessageAsync({ message: prepared.message });
      return api<IageTransferResult>("/market/rewards/iage-transfer", {
        method: "POST",
        body: JSON.stringify({
          recipient: prepared.recipient,
          amount: prepared.amount,
          requestId: prepared.requestId,
          deadline: prepared.deadline,
          signature
        })
      });
    },
    onSuccess: async () => { await market.refetch(); }
  });
  const staticLedger = market.data?.earnings.find((item) => item.earning_type === 1);
  const directLedger = market.data?.earnings.find((item) => item.earning_type === 2);
  const teamLedger = market.data?.earnings.find((item) => item.earning_type === 3);
  const transferredIageLedger = market.data?.earnings.find((item) => item.earning_type === 5);
  const staticAvailable = staticLedger?.available_usdt_gross
    ?? staticLedger?.available_amount ?? "0";
  const dynamicAvailable = sumFixed18(
    directLedger?.available_usdt_gross ?? directLedger?.available_amount,
    teamLedger?.available_usdt_gross ?? teamLedger?.available_amount
  );
  const available = withdrawRewardType === "STATIC" ? staticAvailable : dynamicAvailable;
  const pendingClaim = market.data?.pendingClaims?.find(
    (item) => item.reward_type_name === withdrawRewardType
  );
  const staticSageAvailable = staticLedger?.sage_available_amount ?? "0";
  const staticIageAvailable = staticLedger?.iage_available_amount ?? "0";
  const dynamicSageAvailable = sumFixed18(
    directLedger?.sage_available_amount,
    teamLedger?.sage_available_amount
  );
  const dynamicIageAvailable = sumFixed18(
    directLedger?.iage_available_amount,
    teamLedger?.iage_available_amount,
    transferredIageLedger?.iage_available_amount
  );
  const transferableIage = sumFixed18(staticIageAvailable, dynamicIageAvailable);
  const availableSage = withdrawRewardType === "STATIC"
    ? staticSageAvailable : dynamicSageAvailable;
  const availableIage = withdrawRewardType === "STATIC"
    ? staticIageAvailable : dynamicIageAvailable;
  const rankLevel = market.data?.rank?.rank_level ?? 0;
  const currentWallet = wallet.address?.toLowerCase();
  const myRank = leaderboard.data?.items.find((item) => item.user_address === currentWallet);
  const block = rpcBlockNumber.data != null
    ? `#${rpcBlockNumber.data.toLocaleString("en-US")}`
    : "#—";
  const gasPrice = rpcGasPrice.data != null
    ? `${Number(formatGwei(rpcGasPrice.data)).toLocaleString("en-US", { maximumFractionDigits: 3 })} gwei`
    : "— gwei";

  const signals: Signal[] = useMemo(() => {
    const sagePrice = Number(sageSignalView.currentPrice ?? config.data?.sageQuotePrice ?? 0);
    const latestCandles = sageSignalView.candles ?? [];
    const previousSagePrice = Number(latestCandles.at(-2)?.close ?? sagePrice);
    const sageDelta = previousSagePrice > 0
      ? (sagePrice - previousSagePrice) / previousSagePrice * 100 : 0;
    return [
      { sym: "SAGE", mark: "S", name: textFor(language, "生态代币", "Ecosystem token"), price: sagePrice, delta: sageDelta, share: 12.4, fair: sagePrice * 1.25, index: 78, score: 84 },
      ...(sectorSignals.data?.items ?? []).map((item) => ({
        sym: item.sym,
        mark: item.mark,
        name: textFor(language, item.nameZh, item.nameEn),
        price: item.price,
        delta: item.delta,
        share: item.share,
        fair: item.fair,
        index: item.index,
        score: item.score
      }))
    ];
  }, [config.data, language, sageSignalView, sectorSignals.data]);
  const sortedSignals = [...signals].sort((a, b) => {
    const value = (item: Signal) => sort === "fair" ? item.fair / Math.max(item.price, .0001) : item[sort];
    return (value(a) - value(b)) * direction;
  });
  const leaderboardItems = leaderboard.data?.items ?? [];
  const podium = leaderboardItems.slice(0, 3);
  const board = leaderboardItems.slice(3, rows);
  // 排行榜要等全网出现首批 V1 才有内容；在那之前首页展示网络球而不是空榜单。
  const hasRanking = HOME_LEADERBOARD_VISIBLE
    && leaderboardItems.some((item) => Number(item.rank_level) > 0);

  const selectTab = (next: Tab) => { setTab(next); document.querySelector<HTMLElement>(".sage-scroll")?.scrollTo({ top: 0, behavior: "instant" }); };
  const walletLabel = connecting ? textFor(language, "连接中…", "Connecting…") : session.authenticated && wallet.address ? short(wallet.address) : wallet.isConnected ? textFor(language, "签名登录", "Sign in") : textFor(language, "连接钱包", "Connect");
  const handleWallet = () => {
    if (!wallet.isConnected) return setWalletSheet(true);
    if (!session.authenticated) return session.login.mutate();
    setWalletSheet(true);
  };
  const tokenPocketConnector = connectors.find((connector) =>
    `${connector.id} ${connector.name}`.toLowerCase().includes("tokenpocket")
  ) ?? connectors[0];
  const copyInvite = async () => {
    if (!referral.data?.code) return;
    const copied = await copyText(`${location.origin}/invite?ref=${referral.data.code.referral_code}`);
    setCopyStatus(copied ? "copied" : "failed");
    if (copyResetTimer.current != null) window.clearTimeout(copyResetTimer.current);
    copyResetTimer.current = window.setTimeout(() => setCopyStatus("idle"), copied ? 1600 : 3600);
  };

  return (
    <div className="sage-v4">
      <DecorativeField />
      <header className="sage-header">
        <SageMark />
        <div className="sage-wordmark"><strong>OnChainSage</strong><span>{textFor(language, "链上智策 · 钱包驱动的智能", "WALLET-DRIVEN INTELLIGENCE")}</span></div>
        <div className="sage-languages"><button className={language === "zh" ? "active" : ""} onClick={() => setLanguage("zh")}>中</button><button className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}>EN</button></div>
        <button className="sage-wallet" type="button" onClick={handleWallet}><Wallet size={14} /><span>{walletLabel}</span></button>
      </header>

      <div className="sage-scroll no-scrollbar">
        <section className="sage-agent"><b>›</b><div><p>{tab === "home" ? textFor(language, "Sage 正在协同多个 Agent 实时蒸馏做市策略、汇总收益", "Sage agents are distilling market-making strategies and aggregating returns in real time") : tab === "signals" ? textFor(language, "Sage 正在扫描平台资产与 8 个核心投资板块", "Sage is scanning platform assets and 8 core investment sectors") : textFor(language, "Sage 正在结算你的节点与分红", "Sage is settling your nodes and dividends")}<i /></p><span>{textFor(language, "区块", "Block")} {block}　Gas {gasPrice}</span></div></section>
        <nav className="sage-tabs">{(["home", "signals", "hold"] as Tab[]).map((key, index) => <button key={key} className={tab === key ? "active" : ""} onClick={() => selectTab(key)}>{(language === "zh" ? ["首页", "信号", "持仓"] : ["Home", "Signals", "Holdings"])[index]}</button>)}</nav>
        {tab === "home" && session.authenticated && <HomeAccountOverview
          language={language}
          market={market.data}
          nodeEligibility={nodeEligibility.data}
          referral={referral.data}
          network={referralNetwork.data}
          networkPending={referralNetwork.isLoading}
          networkError={referralNetwork.isError}
          resolved={resolved.data}
          pendingReferral={pendingReferral}
          binding={bindReferral.isPending}
          onBind={() => bindReferral.mutate()}
          copyStatus={copyStatus}
          onCopy={copyInvite}
        />}
        {tab === "home" && (hasRanking
          ? <HomeView language={language} podium={podium} board={board} loading={leaderboard.isLoading} rows={rows} total={leaderboardItems.length} setRows={setRows} />
          : <HomeShowcase language={language} block={block} onGoHold={() => selectTab("hold")} />)}
        {tab === "signals" && <SignalsView
          language={language} signals={sortedSignals} selected={signal} sort={sort}
          direction={direction}
          onSort={(key) => { setDirection(sort === key ? -direction : -1); setSort(key); }}
          onSelect={(symbol) => { setSignal(symbol); setSageDetail(symbol === "SAGE"); }}
          sageDetail={sageDetail} sageInterval={sageInterval} setSageInterval={setSageInterval}
          candles={sageSignalView} candlesPending={sageCandles.isLoading}
          sectorPending={sectorSignals.isLoading} sectorUnavailable={sectorSignals.isError}
          onCloseSage={() => setSageDetail(false)}
        />}
        {tab === "hold" && <HoldingsView key={wallet.address ?? session.account.address} principalWithdrawalBlocked={principalWithdrawalBlocked} language={language} rankLevel={rankLevel} market={market.data} network={referralNetwork.data} node={node.data} nodeWithdrawals={nodeWithdrawals.data ?? []} referral={referral.data} resolved={resolved.data} products={products.data ?? []} selectedCode={selectedCode} setSelectedCode={setSelectedCode} principal={principal} setPrincipal={setPrincipal} quote={quote.data} quotePending={quote.isPending} onQuote={() => quote.mutate()} participationPending={participate.isPending} participationError={friendlyWalletError(participate.error?.message)} onParticipate={() => participate.mutate()} exitConfirmId={exitConfirmId} exitPending={exitPosition.isPending} exitError={friendlyWalletError(exitPosition.error?.message)} onRequestExit={(positionId) => { if (principalWithdrawalBlocked) return; exitPosition.reset(); setExitConfirmId(positionId); }} onCancelExit={() => { exitPosition.reset(); setExitConfirmId(null); }} onConfirmExit={(positionId, contractPositionId, penaltyBps) => exitPosition.mutate({ positionId, contractPositionId, penaltyBps })} staticLedger={staticLedger} historyTab={historyTab} setHistoryTab={setHistoryTab} authenticated={session.authenticated} nodeEligibility={nodeEligibility.data} nodeReferralBound={Boolean(nodeReferral.data?.bound)} nodeUsdtBalance={nodeUsdtBalance.data} nodeUsdtBalancePending={nodeUsdtBalance.isLoading} nodeDepositHash={nodeDepositHash} nodeParticipationPending={participateAsNode.isPending} nodeParticipationError={friendlyWalletError(participateAsNode.error?.message)} onNodeParticipate={() => session.authenticated ? participateAsNode.mutate() : handleWallet()} nodeDirectClaimPending={claimNodeDirect.isPending} nodeDirectClaimError={friendlyWalletError(claimNodeDirect.error?.message)} nodeDirectClaimResult={claimNodeDirect.data} onPrepareNodeDirectClaim={() => claimNodeDirect.reset()} onNodeDirectClaim={(amount) => claimNodeDirect.mutate(amount)} nodePrincipalExitPending={exitNodePrincipal.isPending} nodePrincipalExitError={friendlyWalletError(exitNodePrincipal.error?.message)} nodePrincipalExitResult={exitNodePrincipal.data} onPrepareNodePrincipalExit={() => exitNodePrincipal.reset()} onNodePrincipalExit={() => exitNodePrincipal.mutate()} />}
      </div>

      {tab === "home" && <footer className="sage-dock home-dock"><span className="dock-avatar" /><div>{hasRanking
        ? <><strong>{textFor(language, "我的排名", "Your rank")} <b>#{myRank?.leaderboard_position ?? "—"}</b> · LV.{myRank?.rank_level ?? rankLevel}</strong><small>{textFor(language, "再升 1 级可进入更高等级", "One tier up puts you in a higher band")}</small></>
        : <><strong>{(myRank?.rank_level ?? rankLevel) ? `LV.${myRank?.rank_level ?? rankLevel}` : textFor(language, "未定级", "Unranked")}</strong><small>{textFor(language, "排行榜将在首批 V1 出现后开放", "The leaderboard opens with the first V1 tiers")}</small></>
      }</div><button onClick={() => selectTab("hold")}>{textFor(language, "去提升", "Level up")}</button></footer>}
      {tab === "hold" && <WithdrawDock language={language} rewardType={withdrawRewardType} setRewardType={selectWithdrawalLedger} staticAvailable={staticAvailable} dynamicAvailable={dynamicAvailable} available={available} pendingClaim={pendingClaim} availableSage={availableSage} availableIage={availableIage} sagePrice={sageSignalView.currentPrice} value={withdrawAmount} setValue={setWithdrawAmount} pending={claim.isPending} error={claim.error} done={claim.isSuccess} receivedUsdt={claim.data?.settlementAsset === "USDT" ? claim.data.expectedUsdtNet : undefined} receivedGross={claim.data?.settlementAsset === "USDT" ? claim.data.expectedUsdtGross : undefined} receivedFee={claim.data?.settlementAsset === "USDT" ? claim.data.expectedUsdtFee : undefined} onClaim={() => claim.mutate({ rewardType: withdrawRewardType, amount: withdrawAmount })} onRetryClaim={() => claim.mutate({ rewardType: withdrawRewardType })} transferableIage={transferableIage} iageTransfers={market.data?.iageTransfers ?? []} transferPending={transferIage.isPending} transferError={friendlyWalletError(transferIage.error?.message)} transferResult={transferIage.data} onTransfer={(recipient, amount) => transferIage.mutate({ recipient, amount })} />}

      {walletSheet && <WalletSheet language={language} connected={wallet.isConnected} authenticated={session.authenticated} address={wallet.address} connecting={connecting || session.login.isPending} disconnecting={session.logout.isPending} usdtBalance={nodeUsdtBalance.data} sageBalance={walletSageBalance.data} syncingAssets={syncTestAssets.isPending} assetsSynced={syncTestAssets.isSuccess} assetSyncError={friendlyWalletError(syncTestAssets.error?.message)} onSyncAssets={() => syncTestAssets.mutate()} onClose={() => setWalletSheet(false)} onConnect={() => { if (!wallet.isConnected) tokenPocketConnector && connect({ connector: tokenPocketConnector }); else if (!session.authenticated) session.login.mutate(); }} onDisconnect={() => { session.logout.mutate(); setWalletSheet(false); }} />}
    </div>
  );
}

function SageMark() {
  return <svg className="sage-mark" viewBox="0 0 64 64" aria-label="OnChainSage"><rect width="64" height="64" rx="12" fill="#05070d"/><path d="M17 21 31 32 17 43" fill="none" stroke="#4693ff" strokeOpacity=".22" strokeWidth="13" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 21 31 32 17 43" fill="none" stroke="#4693ff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/><rect x="37" y="37" width="13" height="7" rx="1" fill="#FFB200"/><rect x="1" y="1" width="62" height="62" rx="11" fill="none" stroke="#4693ff" strokeOpacity=".4" strokeWidth="2"/></svg>;
}

function DecorativeField() {
  return <div className="sage-decor" aria-hidden><div className="sage-grid"/><svg viewBox="0 0 390 300" preserveAspectRatio="none"><g stroke="var(--color-accent)" strokeWidth=".7" fill="none"><path d="M22 44 L86 18 L152 52 L214 24 L288 60 L356 30"/><path d="M22 44 L64 108 L152 52 L196 132 L288 60 L342 118"/><path d="M64 108 L196 132 L342 118"/></g><g fill="var(--color-accent)"><circle cx="22" cy="44" r="2.4"/><circle cx="86" cy="18" r="1.8"/><circle cx="152" cy="52" r="2.4"/><circle cx="214" cy="24" r="1.8"/><circle cx="288" cy="60" r="2.4"/><circle cx="356" cy="30" r="1.8"/><circle cx="64" cy="108" r="2"/><circle cx="196" cy="132" r="2"/><circle cx="342" cy="118" r="2"/></g><g stroke="#FFB200" strokeWidth=".8" fill="none" opacity=".55"><rect x="146" y="46" width="12" height="12" rx="2" transform="rotate(45 152 52)"/></g></svg><div className="sage-chain-links">{Array.from({ length: 7 }, (_, index) => <i key={index} className={index < 3 ? "active" : ""}/>)}</div></div>;
}

export function HomeAccountOverview(props: {
  language: Language;
  market?: MarketMe;
  nodeEligibility?: ParticipationEligibility;
  referral?: ReferralMe;
  network?: ReferralNetwork;
  networkPending: boolean;
  networkError: boolean;
  resolved?: ResolvedReferral;
  pendingReferral: string | null;
  binding: boolean;
  onBind: () => void;
  copyStatus: "idle" | "copied" | "failed";
  onCopy: () => void | Promise<void>;
}) {
  const h = (zh: string, en: string) => textFor(props.language, zh, en);
  const [networkExpanded, setNetworkExpanded] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const positions = props.market?.positions.filter((item) => [1, 2].includes(item.position_status)) ?? [];
  const directNodes = props.network?.nodes.filter((item) => item.depth === 1) ?? [];
  const activeDirectNodes = directNodes.filter((item) => item.isActive);
  const directLedger = props.market?.earnings.find((item) => item.earning_type === 2);
  const normalTeam = Number(props.market?.teamRewardTotals?.normal_team_total ?? 0);
  const peerTeam = Number(props.market?.teamRewardTotals?.peer_total ?? 0);
  const teamTotal = normalTeam + peerTeam;
  const smallArea = Number(props.network?.rootPerformance?.smallAreaPerformance
    ?? props.market?.rank?.small_area_performance ?? 0);
  const teamPerformance = Number(props.network?.rootPerformance?.totalTeamPerformance
    ?? props.market?.rank?.total_team_performance ?? 0);
  const invitationType = props.referral?.code?.invitation_type;
  const nodeReferralOwner = isNodeReferralOwner({
    identityChoice: props.nodeEligibility?.identityChoice,
    nodeStatus: props.nodeEligibility?.activeStatus,
    marketIsNodeIdentity: props.market?.account?.is_node_identity,
    ownInvitationType: invitationType
  });
  const inviteLink = props.referral?.code
    ? `${location.origin}/invite?ref=${props.referral.code.referral_code}` : "";
  const relation = props.referral?.relation;
  const detectedNodeInvitation = props.resolved?.invitationType === "NODE";
  const positionCount = positions.length;

  return <main className="home-account-overview sage-content">
    <header className="home-account-heading">
      <div><span>ACCOUNT MAP</span><h1>{h("我的仓位与推荐网络", "My positions & referral network")}</h1></div>
      <em>{positionCount} {h("个有效仓位", "active positions")}</em>
    </header>

    <section className="home-position-overview">
      <header><Hash/><strong>{h("节点与 Market 有效仓位", "Active Node & Market positions")}</strong><em>{positionCount} {h("个", "positions")}</em></header>
      {positions.length ? <div className="home-position-list">{positions.map((position) => <article className="home-position-card" key={position.position_id}>
        <header>
          <span><i className={position.identity_type === 2 ? "is-node" : "is-market"}/><strong>{position.identity_type === 2 ? h("节点 Market 仓位", "Node Market position") : h("Market 仓位", "Market position")}</strong></span>
          <b>{position.lock_days}</b>
        </header>
        <div className="home-position-principal"><span>{h("当前总本金", "Current principal")}</span><strong>${money(position.principal_usdt_equivalent)}</strong></div>
        <div className="home-position-countdown" aria-live="off"><Clock3/><strong>{h("距离可退出", "Exit available in")} {formatPositionCountdown(position.maturity_at, nowMs, props.language)}</strong></div>
        <dl>
          <div><dt>{h("本金构成", "Principal composition")}</dt><dd>{money(position.usdt_amount)} USDT{Number(position.iage_amount) > 0 ? ` + ${money(position.iage_amount)} IAGE` : ""}</dd></div>
          <div><dt>{h("仓位起算", "Position start")}</dt><dd>{formatShanghaiTimestamp(position.started_at, props.language)}</dd></div>
          <div><dt>{h("可退出时间", "Exit time")}</dt><dd>{formatShanghaiTimestamp(position.maturity_at, props.language)}</dd></div>
        </dl>
      </article>)}</div> : <div className="home-empty-state">{h("当前没有有效节点或 Market 仓位", "No active Node or Market position")}</div>}
    </section>

    <section className="home-referral-panel">
      <header><Users/><div><span>MARKET DIRECT</span><strong>{h("Market 直推收益", "Market direct yield")}</strong></div><em>{directNodes.length} {h("个直属关系", "direct relations")}</em></header>
      <div className="home-referral-metrics">
        <div><span>{h("直推收益累计", "Direct yield")}</span><strong>${money(directLedger?.total_earned)}</strong></div>
        <div><span>{h("团队收益", "Team yield")}</span><strong>${money(teamTotal)}</strong><small>{h("团队", "team")} ${money(normalTeam)} · {h("平级/超越", "peer/overtake")} ${money(peerTeam)}</small></div>
        <div><span>{h("团队总业绩", "Team performance")}</span><strong>${money(teamPerformance)}</strong><small>{h("小区业绩", "Small-area performance")} ${money(smallArea)}</small></div>
      </div>
      <div className="home-direct-heading"><span>{h("直属推荐关系", "Direct referrals")}</span><em>{h("地址 · 金额 · 周期 · 到期时间", "Address · principal · term · maturity")}</em></div>
      {directNodes.length ? <div className="home-direct-list">{directNodes.map((node) => <article key={node.address}>
        <div className="home-direct-address"><i className={node.isNodeIdentity ? "is-node" : ""}/><strong>{maskedReferralAddress(node.address)}</strong><em>{node.isNodeIdentity ? h("节点", "Node") : h("普通", "Ordinary")} · {node.isActive ? h("收益中", "Earning") : node.hasActivePosition ? h("待收益生效", "Pending yield") : h("已绑定", "Bound")}</em></div>
        <div className="home-direct-position"><span><b>${money(node.position?.principal)}</b> USDT</span><span><b>{node.position?.lockDays ?? "—"}</b></span><span><b>{node.position?.maturityAt ? String(node.position.maturityAt).slice(0, 10) : "—"}</b></span></div>
        <small>{h("有效期", "Valid through")} {node.position?.firstEarningDate ? String(node.position.firstEarningDate).slice(0, 10) : "—"} — {node.position?.lastEarningDate ? String(node.position.lastEarningDate).slice(0, 10) : "—"}</small>
      </article>)}</div> : <div className="home-empty-state">{h("暂无直属推荐关系", "No direct referral relation")}</div>}

      <button className="team-network-toggle" type="button" aria-expanded={networkExpanded} onClick={() => setNetworkExpanded((current) => !current)}>
        <span><Network/>{networkExpanded ? h("收起团队网络", "Collapse team network") : h("展开团队网络", "Expand team network")}</span>
        <em>{directNodes.length} {h("个直属关系", "direct relations")}{activeDirectNodes.length ? ` · ${activeDirectNodes.length} ${h("收益中", "earning")}` : ""}</em>
        <ChevronDown/>
      </button>
      {networkExpanded && <div className="team-network-panel">
        {props.networkPending ? <div className="home-empty-state">{h("正在读取推荐网络…", "Loading referral network…")}</div>
          : props.networkError ? <div className="home-empty-state is-error">{h("推荐网络暂时无法读取", "Referral network is temporarily unavailable")}</div>
          : directNodes.length ? <section className="team-network-level">
            <header><strong>{h("直推层", "Direct layer")}</strong><em>{directNodes.length} {h("人", "members")}</em></header>
            <div>{directNodes.map((node) => <article key={`direct-${node.address}`} className={node.isActive ? "is-active" : node.hasActivePosition ? "is-pending" : ""}>
              <div><i/><strong>{maskedReferralAddress(node.address)}</strong><em>{node.isActive ? h("收益中", "Earning") : node.hasActivePosition ? h("待生效", "Pending") : h("已绑定", "Bound")}</em></div>
              <dl>
                <div><dt>{h("参与金额", "Principal")}</dt><dd>{node.position ? `$${money(node.position.principal)}` : "—"}</dd></div>
                <div><dt>{h("周期", "Term")}</dt><dd>{node.position?.lockDays ?? "—"}</dd></div>
                <div><dt>{h("到期", "Maturity")}</dt><dd>{node.position?.maturityAt ? String(node.position.maturityAt).slice(0, 10) : "—"}</dd></div>
                <div><dt>{h("团队业绩", "Team performance")}</dt><dd>${money(node.rank.totalTeamPerformance)}</dd></div>
              </dl>
            </article>)}</div>
          </section> : <div className="home-empty-state">{h("暂无直属推荐关系", "No direct referral relation")}</div>}
      </div>}
    </section>

    <section className="home-node-referral">
      <header><Share2/><div><span>{nodeReferralOwner ? "NODE REFERRAL" : "MARKET REFERRAL"}</span><strong>{nodeReferralOwner ? h("节点推荐", "Node referral") : h("Market推荐", "Market referral")}</strong></div><em>{nodeReferralOwner ? h("节点身份", "Node identity") : h("Market身份", "Market identity")}</em></header>
      {!relation ? <div className="home-bind-referral">
        <div><strong>{props.resolved?.valid ? <>{detectedNodeInvitation ? h("检测到节点推荐人", "Node referrer detected") : h("检测到Market推荐人", "Market referrer detected")} <b>{props.resolved.inviter}</b></> : h("尚未绑定推荐人", "No referrer bound")}</strong><small>{props.pendingReferral ? h("绑定关系必须由当前钱包确认后才会生效。", "The relation takes effect only after this wallet confirms it.") : h("请通过有效邀请链接进入并由钱包确认绑定。", "Enter through a valid invitation and confirm it with the wallet.")}</small></div>
        <button type="button" disabled={!props.resolved?.valid || props.binding} onClick={props.onBind}>{props.binding ? h("确认中…", "Confirming…") : h("确认绑定", "Confirm")}</button>
      </div> : <>
        <div className="home-bound-relation"><span>{h("我的推荐人", "My referrer")}</span><strong>{relation.inviter_address}</strong><em>{relation.bound_at ? String(relation.bound_at).slice(0, 10) : h("已绑定", "Bound")}</em></div>
        {props.referral?.code ? <div className="home-invite-link">
          <div><span>{invitationType === "NODE" ? h("我的节点邀请链接", "My node invitation") : h("我的Market邀请链接", "My Market invitation")}</span><input aria-label={h("邀请链接", "Invitation link")} readOnly value={inviteLink} onFocus={(event) => event.currentTarget.select()} onClick={(event) => event.currentTarget.select()}/><small>{props.referral.code.referral_code}</small></div>
          <button type="button" className={props.copyStatus === "failed" ? "copy-failed" : ""} onClick={props.onCopy} aria-live="polite">
            {props.copyStatus === "copied" ? <Check/> : <Copy/>}
            <span>{props.copyStatus === "copied"
              ? h("已复制", "Copied")
              : props.copyStatus === "failed"
                ? h("请点击左侧链接后复制", "Select and copy the link")
                : h("复制邀请链接", "Copy invitation")}</span>
          </button>
        </div> : <div className="home-empty-state">{h("首次参与生效后将生成对应身份的邀请码", "An invitation is generated after the first participation becomes active")}</div>}
      </>}
    </section>
  </main>;
}

function HomeView({ language, podium, board, loading, rows, total, setRows }: { language: Language; podium: LeaderItem[]; board: LeaderItem[]; loading: boolean; rows: number; total: number; setRows: (value: number) => void }) {
  const medals = ["#FFB200", "#c9cbd6", "#b5793f"];
  return <main className="sage-home sage-content"><header className="section-heading"><div><h1>{textFor(language, "持仓等级排行榜", "Holding-tier leaderboard")}</h1><p>{textFor(language, "按持仓等级排序；等级相同时，团队总业绩更高者靠前（业绩数值不公开）", "Ranked by tier; ties go to the higher team volume (the figure itself stays private)")}</p></div><span>TOP 100</span></header>{loading ? <div className="v4-loading">{textFor(language, "正在读取链上等级快照…", "Reading the on-chain tier snapshot…")}</div> : <><section className="v4-podium">{podium.map((item, index) => <article key={item.user_address} className={index === 0 ? "winner" : ""}><b style={{ color: medals[index] }}>#{index + 1}</b><i style={{ "--avatar-hue": hue(item.user_address) } as CSSProperties}/><span>{short(item.user_address)}</span><em>LV.{item.rank_level}</em></article>)}</section><div className="board-head"><span>#</span><span>{textFor(language, "用户", "User")}</span><span>{textFor(language, "等级", "Tier")}</span></div><section className="v4-board">{board.map((item) => <article key={item.user_address}><b>{item.leaderboard_position}</b><i style={{ "--avatar-hue": hue(item.user_address) } as CSSProperties}/><div><strong>{short(item.user_address)}</strong><small>{textFor(language, "达成于", "since")} {String(item.settlement_date).slice(0, 7)}</small></div>{Number(item.tie_count) > 1 && <span className="tie"><Users size={12}/>{textFor(language, "同级末位", "tier cutoff")}</span>}<em>LV.{item.rank_level}</em></article>)}</section><button className="load-board" onClick={() => setRows(Math.min(100, rows + 20))}>{rows >= Math.min(100, total) ? textFor(language, "已显示全部", "All shown") : textFor(language, "加载更多", "Load more")}</button></>}</main>;
}

/**
 * 首页主视觉：Sage 对全网关系图做实时推理的可视化。
 *
 * 中心是推理核（同心环 + 辉光），外层是参与地址构成的球面网络，三道倾斜轨道环表示
 * 模型的分层结构。亮度以「从核心向外扩散的推理波」驱动：波峰扫到哪一层，哪一层的
 * 节点和边就被点亮，视觉上就是一次推理沿关系树逐层传播。另有一道旋转扫描角，扫到
 * 的节点额外提亮，以及沿边流动的结算脉冲。
 *
 * 全部用 Canvas 2D 手写投影 + 叠加混色（lighter）做辉光，不引入任何 3D / 动画依赖。
 * prefers-reduced-motion 下只画一帧静态构图。
 */
function SageNetworkOrb() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const styles = getComputedStyle(canvas);
    const accent = styles.getPropertyValue("--color-accent").trim() || "#9184d9";
    const bright = styles.getPropertyValue("--color-accent-200").trim() || "#e7e5fe";
    const deep = styles.getPropertyValue("--color-accent-600").trim() || "#7569b8";
    const NODE_COUNT = 72;

    // 斐波那契球面：分布均匀，不会像随机撒点那样结块。
    const nodes = Array.from({ length: NODE_COUNT }, (_, index) => {
      const y = 1 - (index / (NODE_COUNT - 1)) * 2;
      const ring = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = index * Math.PI * (3 - Math.sqrt(5));
      return {
        x: Math.cos(theta) * ring,
        y,
        z: Math.sin(theta) * ring,
        // 与核心的角距离决定推理波到达它的时刻，形成由内向外的传播。
        phase: Math.acos(Math.max(-1, Math.min(1, y))) / Math.PI
      };
    });

    // 每个节点连最近的两个邻居：稀疏、不杂乱，读起来像一张关系树。
    const edgeKeys = new Set<string>();
    const edges: Array<[number, number]> = [];
    nodes.forEach((node, index) => {
      nodes
        .map((other, otherIndex) => ({
          otherIndex,
          distance: (other.x - node.x) ** 2 + (other.y - node.y) ** 2 + (other.z - node.z) ** 2
        }))
        .filter((item) => item.otherIndex !== index)
        .sort((left, right) => left.distance - right.distance)
        .slice(0, 2)
        .forEach((item) => {
          const key = index < item.otherIndex
            ? `${index}:${item.otherIndex}` : `${item.otherIndex}:${index}`;
          if (edgeKeys.has(key)) return;
          edgeKeys.add(key);
          edges.push([index, item.otherIndex]);
        });
    });

    // 沿关系链流动的结算脉冲，错开相位避免同时到达。
    const pulses = [0, 1, 2, 3].map((index) => ({
      edge: Math.floor(index * edges.length / 4),
      progress: index / 4
    }));

    // 邻接表：智能体靠它在关系图上自主选择下一跳。
    const neighbours = nodes.map<number[]>(() => []);
    for (const [from, to] of edges) {
      neighbours[from]!.push(to);
      neighbours[to]!.push(from);
    }

    // 八个自主智能体分工遍历关系、归集、评级和风控：抵达后取样，再跳向下一邻居。
    // 拖尾保留最近若干个屏幕坐标，画成渐隐折线，读起来像一条正在爬取的轨迹。
    const AGENT_COUNT = 8;
    const AGENT_TINTS = [bright, accent, deep, "#4693ff"];
    const agents = Array.from({ length: AGENT_COUNT }, (_, index) => {
      const from = Math.floor(index * NODE_COUNT / AGENT_COUNT);
      return {
        role: index,
        tint: AGENT_TINTS[index % AGENT_TINTS.length]!,
        from,
        to: neighbours[from]![0] ?? (from + 1) % NODE_COUNT,
        progress: (index * 0.173) % 1,
        speed: 0.00034 + (index % 4) * 0.000065,
        dwell: 0,
        trail: [] as Array<{ x: number; y: number }>
      };
    });

    // 三道倾斜轨道环：模型的分层结构。
    const orbits = [
      { radius: 1.24, tiltX: 0.62, tiltZ: 0.2, speed: 0.9 },
      { radius: 1.05, tiltX: -0.34, tiltZ: 1.05, speed: -1.35 },
      { radius: 1.42, tiltX: 1.24, tiltZ: -0.5, speed: 0.55 }
    ];

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let frame = 0;

    const resize = () => {
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(width * ratio));
      canvas.height = Math.max(1, Math.round(height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const draw = (time: number, spin: number) => {
      if (!width || !height) return;
      context.clearRect(0, 0, width, height);
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) * 0.34;
      const tilt = -0.42;
      const cosSpin = Math.cos(spin);
      const sinSpin = Math.sin(spin);
      const cosTilt = Math.cos(tilt);
      const sinTilt = Math.sin(tilt);
      const scan = (time * 0.00042) % (Math.PI * 2);
      const wave = (time * 0.00055) % 1;

      const project = (x: number, y: number, z: number, spread: number) => {
        const rx = x * cosSpin - z * sinSpin;
        const rz = x * sinSpin + z * cosSpin;
        const ry = y * cosTilt - rz * sinTilt;
        const depth = y * sinTilt + rz * cosTilt;
        const scale = 1 / (1.9 - depth * 0.55);
        return {
          screenX: centerX + rx * radius * scale * spread,
          screenY: centerY + ry * radius * scale * spread,
          depth
        };
      };

      // ---- 推理核的辉光 ----
      const corePulse = 0.5 + Math.sin(time * 0.0016) * 0.5;
      const glow = context.createRadialGradient(
        centerX, centerY, 0, centerX, centerY, radius * (1.15 + corePulse * 0.12)
      );
      glow.addColorStop(0, `${accent}44`);
      glow.addColorStop(0.45, `${accent}14`);
      glow.addColorStop(1, "transparent");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);

      context.globalCompositeOperation = "lighter";

      // ---- 轨道环 ----
      orbits.forEach((orbit, index) => {
        const steps = 72;
        context.strokeStyle = index === 1 ? bright : accent;
        context.lineWidth = index === 1 ? 0.9 : 0.7;
        context.globalAlpha = 0.3 - index * 0.06;
        context.beginPath();
        for (let step = 0; step <= steps; step += 1) {
          const angle = (step / steps) * Math.PI * 2 + spin * orbit.speed;
          const ox = Math.cos(angle) * orbit.radius;
          const oz = Math.sin(angle) * orbit.radius;
          const y = oz * Math.sin(orbit.tiltX);
          const z = oz * Math.cos(orbit.tiltX);
          const x = ox * Math.cos(orbit.tiltZ) - y * Math.sin(orbit.tiltZ);
          const point = project(x, y * Math.cos(orbit.tiltZ) + ox * Math.sin(orbit.tiltZ), z, 1.55);
          if (step === 0) context.moveTo(point.screenX, point.screenY);
          else context.lineTo(point.screenX, point.screenY);
        }
        context.stroke();
      });

      const projected = nodes.map((node) => {
        const point = project(node.x, node.y, node.z, 1.55);
        // 推理波：波峰扫过该节点所在层时点亮，宽度 0.18 让亮带清晰但不刺眼。
        const distance = Math.abs(((node.phase - wave) % 1 + 1) % 1);
        const fire = Math.max(0, 1 - Math.min(distance, 1 - distance) / 0.18);
        const angle = Math.atan2(point.screenY - centerY, point.screenX - centerX);
        const sweepDelta = Math.abs(((angle - scan) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
        const swept = Math.max(0, 1 - sweepDelta / 0.55);
        return { ...point, fire, swept };
      });

      // ---- 关系边：两端都被点亮时整条边发光 ----
      for (const [from, to] of edges) {
        const start = projected[from]!;
        const end = projected[to]!;
        const depth = (start.depth + end.depth) / 2;
        const lit = Math.min(start.fire, end.fire);
        context.globalAlpha = 0.04 + Math.max(0, depth + 1) * 0.1 + lit * 0.5;
        context.strokeStyle = lit > 0.25 ? bright : deep;
        context.lineWidth = 0.6 + lit * 1;
        context.beginPath();
        context.moveTo(start.screenX, start.screenY);
        context.lineTo(end.screenX, end.screenY);
        context.stroke();
      }

      // ---- 结算脉冲 ----
      for (const pulse of pulses) {
        const [from, to] = edges[pulse.edge % edges.length]!;
        const start = projected[from]!;
        const end = projected[to]!;
        const x = start.screenX + (end.screenX - start.screenX) * pulse.progress;
        const y = start.screenY + (end.screenY - start.screenY) * pulse.progress;
        const depth = start.depth + (end.depth - start.depth) * pulse.progress;
        const alpha = 0.3 + Math.max(0, depth + 1) * 0.35;
        const trail = context.createRadialGradient(x, y, 0, x, y, 5);
        trail.addColorStop(0, bright);
        trail.addColorStop(0.45, `${accent}55`);
        trail.addColorStop(1, "transparent");
        context.globalAlpha = alpha;
        context.fillStyle = trail;
        context.beginPath();
        context.arc(x, y, 5, 0, Math.PI * 2);
        context.fill();
      }

      // ---- 节点：后到前绘制，近处自然盖住远处 ----
      for (const point of [...projected].sort((left, right) => left.depth - right.depth)) {
        const near = Math.max(0, point.depth + 1) / 2;
        const energy = Math.max(point.fire, point.swept * 0.7);
        context.globalAlpha = 0.2 + near * 0.6 + energy * 0.4;
        context.fillStyle = energy > 0.3 ? bright : near > 0.62 ? accent : deep;
        const size = 0.9 + near * 1.8 + energy * 1.5;
        if (energy > 0.3) {
          const halo = context.createRadialGradient(
            point.screenX, point.screenY, 0, point.screenX, point.screenY, size * 2.6
          );
          halo.addColorStop(0, `${bright}70`);
          halo.addColorStop(0.5, `${accent}30`);
          halo.addColorStop(1, "transparent");
          context.fillStyle = halo;
          context.beginPath();
          context.arc(point.screenX, point.screenY, size * 2.6, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = bright;
        }
        context.beginPath();
        context.arc(point.screenX, point.screenY, size, 0, Math.PI * 2);
        context.fill();
      }

      // ---- 智能体：拖尾轨迹 + 本体 + 取样环 ----
      const agentPoints = agents.map((agent) => {
        const start = projected[agent.from]!;
        const end = projected[agent.to]!;
        const eased = agent.dwell > 0 ? 1 : agent.progress;
        return {
          agent,
          x: start.screenX + (end.screenX - start.screenX) * eased,
          y: start.screenY + (end.screenY - start.screenY) * eased,
          depth: start.depth + (end.depth - start.depth) * eased
        };
      });
      for (const point of agentPoints) {
        const { agent } = point;
        agent.trail.push({ x: point.x, y: point.y });
        if (agent.trail.length > 20) agent.trail.shift();
        context.strokeStyle = agent.tint;
        context.lineWidth = 1.1;
        for (let index = 1; index < agent.trail.length; index += 1) {
          context.globalAlpha = (index / agent.trail.length) * 0.32;
          context.beginPath();
          context.moveTo(agent.trail[index - 1]!.x, agent.trail[index - 1]!.y);
          context.lineTo(agent.trail[index]!.x, agent.trail[index]!.y);
          context.stroke();
        }
        const near = Math.max(0, point.depth + 1) / 2;
        // 停留取样时画一圈扩散环，表示正在读取该地址。
        if (agent.dwell > 0) {
          const spread = 1 - agent.dwell / 260;
          context.globalAlpha = (1 - spread) * 0.5 * (0.4 + near);
          context.strokeStyle = agent.tint;
          context.lineWidth = 0.9;
          context.beginPath();
          context.arc(point.x, point.y, 3 + spread * 9, 0, Math.PI * 2);
          context.stroke();
        }
        const halo = context.createRadialGradient(point.x, point.y, 0, point.x, point.y, 9);
        halo.addColorStop(0, `${agent.tint}90`);
        halo.addColorStop(1, "transparent");
        context.globalAlpha = 0.45 + near * 0.45;
        context.fillStyle = halo;
        context.beginPath();
        context.arc(point.x, point.y, 9, 0, Math.PI * 2);
        context.fill();
        const agentSize = 2 + near * 1.25;
        context.fillStyle = agent.tint;
        context.strokeStyle = bright;
        context.lineWidth = 0.55;
        context.globalAlpha = 0.62 + near * 0.36;
        context.save();
        context.translate(point.x, point.y);
        context.rotate(time * 0.00045 + agent.role * 0.72);
        context.beginPath();
        if (agent.role % 3 === 0) {
          context.rect(-agentSize, -agentSize, agentSize * 2, agentSize * 2);
        } else if (agent.role % 3 === 1) {
          context.moveTo(0, -agentSize * 1.35);
          context.lineTo(agentSize * 1.2, agentSize);
          context.lineTo(-agentSize * 1.2, agentSize);
          context.closePath();
        } else {
          context.arc(0, 0, agentSize, 0, Math.PI * 2);
        }
        context.fill();
        context.stroke();
        context.restore();
      }
      // 智能体互相靠近时画一条协同连线。
      for (let left = 0; left < agentPoints.length; left += 1) {
        for (let right = left + 1; right < agentPoints.length; right += 1) {
          const a = agentPoints[left]!;
          const b = agentPoints[right]!;
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          if (distance > radius * 0.85) continue;
          context.globalAlpha = (1 - distance / (radius * 0.85)) * 0.3;
          context.strokeStyle = bright;
          context.lineWidth = 0.6;
          context.setLineDash([2, 3]);
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
          context.setLineDash([]);
        }
      }

      // ---- 推理核 ----
      for (let ring = 0; ring < 3; ring += 1) {
        const ringRadius = radius * (0.1 + ring * 0.07) * (1 + corePulse * 0.16);
        context.globalAlpha = 0.5 - ring * 0.14;
        context.strokeStyle = bright;
        context.lineWidth = 1 - ring * 0.22;
        context.beginPath();
        context.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
        context.stroke();
      }
      context.globalAlpha = 0.55 + corePulse * 0.45;
      const core = context.createRadialGradient(
        centerX, centerY, 0, centerX, centerY, radius * 0.16
      );
      core.addColorStop(0, bright);
      core.addColorStop(0.4, `${accent}aa`);
      core.addColorStop(1, "transparent");
      context.fillStyle = core;
      context.beginPath();
      context.arc(centerX, centerY, radius * 0.16, 0, Math.PI * 2);
      context.fill();

      context.globalCompositeOperation = "source-over";
      context.globalAlpha = 1;
    };

    resize();
    if (reduced) {
      draw(2200, 0.6);
    } else {
      let elapsed = 0;
      let previous = performance.now();
      const tick = (now: number) => {
        const delta = Math.min(80, now - previous);
        previous = now;
        elapsed += delta;
        for (const pulse of pulses) {
          pulse.progress += delta * 0.0009;
          if (pulse.progress >= 1) {
            pulse.progress = 0;
            pulse.edge = (pulse.edge + 7) % edges.length;
          }
        }
        for (const agent of agents) {
          if (agent.dwell > 0) {
            agent.dwell -= delta;
            continue;
          }
          agent.progress += delta * agent.speed;
          if (agent.progress < 1) continue;
          // 抵达后停留一小段（取样），再从邻居里挑一个不是来路的继续走。
          agent.progress = 0;
          agent.dwell = 190 + (agent.role % 3) * 55;
          const options = neighbours[agent.to]!.filter((item) => item !== agent.from);
          const next = (options.length ? options : neighbours[agent.to]!)[
            Math.floor(elapsed / 97) % Math.max(1, options.length || neighbours[agent.to]!.length)
          ] ?? agent.from;
          agent.from = agent.to;
          agent.to = next;
        }
        draw(elapsed, elapsed * 0.00016);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }

    const observer = new ResizeObserver(() => {
      resize();
      if (reduced) draw(2200, 0.6);
    });
    observer.observe(canvas);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="sage-orb-canvas" aria-hidden />;
}

/** 推理步骤走马灯。每一条都是结算管线里真实发生的动作，不是编造的模型指标。 */
function SageReasoningTicker({ language }: { language: Language }) {
  const steps = useMemo(() => {
    return language === "zh" ? [
      "解析全网推荐关系树",
      "逐条市场线归集小区累计业绩",
      "比对下方最高等级，判定级差分支",
      "汇总直推 10% 与平级 / 被超越 10%",
      "校验三倍出局上限与重新开仓资格",
      "生成次日 0:00 的结算批次"
    ] : [
      "Parsing the network referral tree",
      "Aggregating weak-leg volume line by line",
      "Comparing the highest tier below to pick the branch",
      "Summing the 10% direct and 10% peer rewards",
      "Verifying the 3× exit cap and reopening eligibility",
      "Preparing the 00:00 settlement batch"
    ];
  }, [language]);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIndex((value) => (value + 1) % steps.length), 2600);
    return () => clearInterval(timer);
  }, [steps.length]);
  return <div className="sage-reasoning">
    <i />
    <span key={index}>{steps[index]}</span>
    <em>{String(index + 1).padStart(2, "0")}/{String(steps.length).padStart(2, "0")}</em>
  </div>;
}

function HomeShowcase({ language, block, onGoHold }: {
  language: Language; block: string | number; onGoHold: () => void;
}) {
  const h = (zh: string, en: string) => textFor(language, zh, en);
  return <main className="sage-home sage-content">
    <header className="section-heading">
      <div>
        <h1>{h("现实世界资产网络 · 实时推理", "Real-world asset network · live inference")}</h1>
        <p>{h("节点是智能体，连线是协作关系。通过专业量化实时操作，每日 0:00 结算。", "Nodes are agents and links are collaboration paths. Professional quantitative strategies operate in real time, with daily settlement at 00:00.")}</p>
      </div>
      <span>LIVE</span>
    </header>
    <section className="sage-orb">
      <div className="sage-orb-badge"><Sparkles size={11} />{h("SAGE 推理引擎", "SAGE inference")}</div>
      <SageNetworkOrb />
      <SageReasoningTicker language={language} />
      <div className="sage-orb-stats">
        <div><span>{h("协同 Agent", "Agent mesh")}</span><strong>100+</strong></div>
        <div><span>{h("等级体系", "Tier ladder")}</span><strong>V1–V7</strong></div>
        <div><span>{h("当前区块", "Block")}</span><strong>{block}</strong></div>
      </div>
    </section>
    <section className="sage-orb-note">
      <Medal />
      <div>
        <strong>{h("等级排行榜尚未开放", "The tier leaderboard is not open yet")}</strong>
      </div>
    </section>
    <button className="load-board" onClick={onGoHold}>{h("查看我的等级进度", "See my tier progress")}</button>
  </main>;
}

function SageCandleChart({ language, data, pending }: {
  language: Language; data?: SageCandles; pending: boolean;
}) {
  const candles = data?.candles ?? [];
  if (pending) return <div className="sage-chart-empty">{textFor(language, "正在同步 SAGE 实时价格…", "Syncing the live SAGE price…")}</div>;
  if (!candles.length) return <div className="sage-chart-empty"><BarChart3/><strong>{textFor(language, "SAGE 实时价格暂不可用", "The live SAGE price is unavailable")}</strong><span>{textFor(language, "请稍后重新加载", "Please reload shortly")}</span></div>;
  const values = candles.flatMap((item) => [Number(item.high), Number(item.low)]);
  const lowest = Math.min(...values);
  const highest = Math.max(...values);
  const intraday = data?.interval === "1m" || data?.interval === "5m" || data?.interval === "15m";
  const padding = Math.max((highest - lowest) * .09, highest * (intraday ? .0015 : .003), .000001);
  const min = lowest - padding;
  const max = highest + padding;
  const width = 340;
  const height = 178;
  const plotTop = 16;
  const plotBottom = 146;
  const plotLeft = 8;
  const plotRight = 298;
  const candleWidth = Math.max(3, Math.min(7, (plotRight - plotLeft) / candles.length * .74));
  const xAt = (index: number) => plotLeft + index * ((plotRight - plotLeft) / Math.max(1, candles.length - 1));
  const yAt = (price: number) => plotBottom - ((price - min) / (max - min)) * (plotBottom - plotTop);
  const fullHistorySpan = candles.length > 1
    ? Date.parse(candles.at(-1)!.time) - Date.parse(candles[0]!.time)
    : 0;
  const formatAxisTime = (value: string) => new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : "en-US", data?.interval === "all"
    ? fullHistorySpan < 86_400_000
      ? { hour: "2-digit", minute: "2-digit", hour12: false }
      : { year: "2-digit", month: "2-digit", day: "2-digit" }
    : data?.interval === "1d"
      ? { month: "2-digit", day: "2-digit" }
    : intraday
      ? { hour: "2-digit", minute: "2-digit", hour12: false }
      : { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
  const tickCount = intraday ? 5 : 4;
  const timeTicks = Array.from({ length: tickCount }, (_, index) => Math.round(index * (candles.length - 1) / (tickCount - 1)));
  return <svg className="sage-candle-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={textFor(language, "SAGE 对 USDT K 线", "SAGE/USDT candles")}>
    {[0, 1, 2, 3].map((line) => {
      const y = plotTop + line * ((plotBottom - plotTop) / 3);
      const price = max - line * ((max - min) / 3);
      return <g key={line}><line x1={plotLeft} x2={plotRight} y1={y} y2={y} className="sage-chart-grid"/><text x="304" y={y + 2.5} textAnchor="start" className="sage-chart-axis sage-chart-price">${money(price, 3)}</text></g>;
    })}
    {timeTicks.map((index) => <line key={`time-grid-${index}`} x1={xAt(index)} x2={xAt(index)} y1={plotTop} y2={plotBottom} className="sage-chart-grid sage-chart-time-grid"/>)}
    {candles.map((item, index) => {
      const open = Number(item.open); const close = Number(item.close);
      const high = Number(item.high); const low = Number(item.low);
      const up = close >= open; const x = xAt(index);
      const bodyTop = yAt(Math.max(open, close));
      const bodyHeight = Math.max(1.4, Math.abs(yAt(open) - yAt(close)));
      return <g key={`${item.time}-${index}`} className={up ? "candle-up" : "candle-down"}>
        <title>{`${new Date(item.time).toLocaleString()} O ${money(open, 4)} H ${money(high, 4)} L ${money(low, 4)} C ${money(close, 4)}`}</title>
        <line x1={x} x2={x} y1={yAt(high)} y2={yAt(low)}/>
        <rect x={x - candleWidth / 2} y={bodyTop} width={candleWidth} height={bodyHeight} rx=".3"/>
      </g>;
    })}
    {timeTicks.map((index, tickIndex) => <text key={`time-${index}`} x={xAt(index)} y="166" textAnchor={tickIndex === 0 ? "start" : tickIndex === timeTicks.length - 1 ? "end" : "middle"} className="sage-chart-axis sage-chart-time">{formatAxisTime(candles[index]!.time)}</text>)}
  </svg>;
}

function SignalsView({ language, signals, selected, sort, direction, onSort, onSelect,
  sageDetail, sageInterval, setSageInterval, candles, candlesPending,
  sectorPending, sectorUnavailable, onCloseSage }: {
  language: Language; signals: Signal[]; selected: string; sort: string; direction: number;
  onSort: (key: "share" | "fair" | "index" | "score") => void;
  onSelect: (sym: string) => void; sageDetail: boolean;
  sageInterval: SageInterval;
  setSageInterval: (value: SageInterval) => void;
  candles?: SageCandles; candlesPending: boolean;
  sectorPending: boolean; sectorUnavailable: boolean;
  onCloseSage: () => void;
}) {
  const metricLabels = language === "zh" ? ["市占率", "估值", "指数", "评分"] : ["Share", "Fair", "Index", "Score"];
  const view = candles;
  const sectors = signals.filter((item) => item.sym !== "SAGE");
  const groups = [
    {
      kind: "token", title: textFor(language, "生态代币", "Ecosystem token"),
      sub: textFor(language, "SAGE · 实时收益波动率", "SAGE · live yield volatility"),
      data: signals.filter((item) => item.sym === "SAGE")
    },
    {
      kind: "sectors", title: textFor(language, "AI 板块信号", "AI sector signals"),
      sub: "",
      data: sectors
    },
    {
      kind: "coming", title: textFor(language, "更多潜力板块敬请期待", "More high-potential sectors coming soon"),
      sub: "",
      data: [] as Signal[]
    }
  ];
  return <main className="sage-signals sage-content"><div className="sort-chips">{(["share", "fair", "index", "score"] as const).map((key, index) => <button key={key} className={sort === key ? "active" : ""} onClick={() => onSort(key)}>{metricLabels[index]}{sort === key ? direction === -1 ? " ↓" : " ↑" : ""}</button>)}</div>{sageDetail && <section className="sage-market-detail"><header><div><span>SAGE / USDT</span><strong>${money(view?.currentPrice)}</strong><small>{textFor(language, "实时价格", "Live price")}</small></div><button type="button" onClick={onCloseSage} aria-label={textFor(language, "关闭 SAGE 详情", "Close SAGE detail")}><X/></button></header><div className="sage-periods">{SAGE_INTERVALS.map((value) => <button key={value} className={sageInterval === value ? "active" : ""} onClick={() => setSageInterval(value)}>{SAGE_INTERVAL_LABELS[value][language]}</button>)}</div><SageCandleChart language={language} data={view} pending={candlesPending}/></section>}{groups.map((group) => <section className="signal-group" key={group.title}><header><strong>{group.title}</strong><span>{group.sub}</span></header>{group.kind === "sectors" && group.data.length === 0 && <div className="signal-source-state">{sectorPending ? textFor(language, "板块行情同步中…", "Syncing sector quotes…") : sectorUnavailable ? textFor(language, "板块行情暂不可用", "Sector quotes are temporarily unavailable") : textFor(language, "等待板块行情", "Waiting for sector quotes")}</div>}{group.kind === "coming" && <div className="signal-coming-soon"><Sparkles/><span>SAGE · COMING SOON</span></div>}{group.data.map((asset) => <button className={selected === asset.sym ? "signal-card active" : "signal-card"} data-signal={asset.sym} key={asset.sym} onClick={() => onSelect(asset.sym)}><div className="signal-main"><i>{asset.mark}</i><div><strong>{asset.sym}<span> · {asset.name}</span></strong><small>${money(asset.price)}　<b className={asset.delta >= 0 ? "up" : "down"}>{asset.delta >= 0 ? "+" : ""}{asset.delta.toFixed(1)}%</b></small></div><em>{asset.score}<small>{metricLabels[3]}</small></em></div><div className="signal-metrics">{[[metricLabels[0], `${asset.share.toFixed(1)}%`, asset.share * 2.2], [metricLabels[1], `$${money(asset.fair)}`, Math.min(100, asset.fair / Math.max(asset.price, .01) * 62)], [metricLabels[2], asset.index, asset.index], [metricLabels[3], asset.score, asset.score]].map(([key, value, width]) => <div key={String(key)}><span>{key}</span><strong>{value}</strong><i><b style={{ width: `${width}%` }}/></i></div>)}</div></button>)}</section>)}</main>;
}

export function HoldingsView(props: {
  language: Language; rankLevel: number; market?: MarketMe; network?: ReferralNetwork; node?: Assets; nodeWithdrawals: NodeWithdrawal[]; referral?: ReferralMe; resolved?: ResolvedReferral;
  products: Product[]; selectedCode: ProductCode; setSelectedCode: (code: ProductCode) => void;
  principal: string; setPrincipal: (value: string) => void; iagePercent?: number; setIagePercent?: (value: number) => void; quote?: Quote; quotePending: boolean; onQuote: () => void;
  participationPending: boolean; participationError?: string; onParticipate: () => void; staticLedger?: MarketMe["earnings"][number]; historyTab: "all" | "in" | "out"; setHistoryTab: (value: "all" | "in" | "out") => void;
  exitConfirmId: string | null; exitPending: boolean; exitError?: string; principalWithdrawalBlocked: boolean;
  onRequestExit: (positionId: string) => void; onCancelExit: () => void;
  onConfirmExit: (positionId: string, contractPositionId: string, penaltyBps: number) => void;
  authenticated: boolean; nodeEligibility?: ParticipationEligibility; nodeReferralBound: boolean;
  nodeUsdtBalance?: bigint; nodeUsdtBalancePending: boolean; nodeDepositHash: `0x${string}` | null;
  nodeParticipationPending: boolean; nodeParticipationError?: string; onNodeParticipate: () => void;
  nodeDirectClaimPending: boolean; nodeDirectClaimError?: string;
  nodeDirectClaimResult?: NodeDirectClaim;
  onPrepareNodeDirectClaim: () => void; onNodeDirectClaim: (amount: string) => void;
  nodePrincipalExitPending: boolean; nodePrincipalExitError?: string;
  nodePrincipalExitResult?: NodePrincipalExit;
  onPrepareNodePrincipalExit: () => void; onNodePrincipalExit: () => void;
}) {
  const h = (zh: string, en: string) => textFor(props.language, zh, en);
  const active = props.market?.positions.find((item) => [1, 2, 6].includes(item.position_status));
  const isBreachFrozen = Boolean(active?.breach_exit_id);
  const isTopUp = active?.position_status === 1 && !isBreachFrozen;
  const isExitPending = isBreachFrozen || active?.position_status === 2 || active?.position_status === 6;
  const isForcedExitPending = active?.position_status === 6;
  const hasConfirmedMarketExit = Boolean(
    props.market?.positions.some((item) => [3, 4, 5].includes(item.position_status))
  );
  // A permanent NODE identity only blocks the ordinary entry while its first
  // node-backed Market position is still waiting to be indexed. Once an old
  // position has reached a terminal state, the retained identity/rank must not
  // prevent the user from opening a fresh 7/90/180-day Market position.
  const nodeAwaitingInitialMarketSync = !active
    && props.nodeEligibility?.identityChoice === "NODE"
    && !hasConfirmedMarketExit;
  const termSelectionLocked = Boolean(active);
  const [termMenuOpen, setTermMenuOpen] = useState(false);
  const termMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!termMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!termMenuRef.current?.contains(event.target as Node)) setTermMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTermMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [termMenuOpen]);
  useEffect(() => {
    if (termSelectionLocked) setTermMenuOpen(false);
  }, [termSelectionLocked]);
  const activeEarned = Number(active?.cumulative_static_earning ?? 0) + Number(active?.cumulative_dynamic_earning ?? 0);
  const activeCap = Number(active?.earning_cap_amount ?? 0);
  const activeProgress = activeCap > 0 ? Math.min(100, activeEarned / activeCap * 100) : 0;
  const exitUsdtPrincipal = Number(active?.market_usdt_amount ?? active?.usdt_amount ?? 0);
  const totalIagePrincipal = Number(active?.market_iage_amount ?? active?.iage_amount ?? 0);
  // The prospective V2 policy writes off matured internal IAGE. Older policy
  // responses and V1 positions retain their existing refund preview.
  const internalIagePrincipal = totalIagePrincipal;
  const contractPositionId = String(active?.contract_position_id ?? "");
  const v2IageExitPolicy = /^\d+$/.test(contractPositionId)
    && BigInt(contractPositionId) >= 1_000_000_000_000n
    && props.market?.iagePrincipalExitPolicy?.chainId === appChain.id
      ? props.market.iagePrincipalExitPolicy : null;
  const writeOffIagePrincipal = v2IageExitPolicy?.mode === "WRITE_OFF"
    && v2IageExitPolicy.active === true;
  const iageRefundPreviewKnown = !v2IageExitPolicy || v2IageExitPolicy.active !== null;
  // 本金随时可退：到期无违约费；周期内退出扣 15% 违约管理费（由独立储备转入 Treasury），
  // 剩余 85% 再按 1% 收取提现手续费。
  const exitReady = Number(active?.normal_exit_available ?? 0) === 1;
  const awaitingNormalExit = active?.position_status === 2 && !exitReady;
  const [exitCountdownNow, setExitCountdownNow] = useState(() => Date.now());
  useEffect(() => {
    if (!awaitingNormalExit) return;
    const timer = window.setInterval(() => setExitCountdownNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [awaitingNormalExit]);
  const exitPenaltyBps = exitReady ? 0 : 1500;
  const exitUsdtPenalty = exitUsdtPrincipal * exitPenaltyBps / 10000;
  const exitIagePenalty = internalIagePrincipal * exitPenaltyBps / 10000;
  const exitUsdtRefundable = exitUsdtPrincipal - exitUsdtPenalty;
  const exitIageRefundable = internalIagePrincipal - exitIagePenalty;
  const exitUsdtFee = exitUsdtRefundable * .01;
  const exitIageFee = exitIageRefundable * .01;
  const exitUsdtNet = exitUsdtRefundable - exitUsdtFee;
  const exitIageNet = exitIageRefundable - exitIageFee;
  const [nodeDirectClaimReview, setNodeDirectClaimReview] = useState(false);
  const [nodeDirectClaimAmount, setNodeDirectClaimAmount] = useState("");
  const [nodePrincipalExitReview, setNodePrincipalExitReview] = useState(false);
  const nodeDirectAvailableRaw = fixed18Units(props.node?.directReferral.availableBalance);
  const nodeDirectRemainingRaw = fixed18Units(props.node?.remainingEarningWithdrawal);
  const nodeDirectMaxRaw = nodeDirectAvailableRaw < nodeDirectRemainingRaw
    ? nodeDirectAvailableRaw : nodeDirectRemainingRaw;
  const nodeDirectMax = formatUnits(nodeDirectMaxRaw, 18);
  const nodeDirectRequestedRaw = parseFixed18Input(nodeDirectClaimAmount);
  const nodeDirectClaimValid = nodeDirectRequestedRaw != null
    && nodeDirectRequestedRaw > 0n && nodeDirectRequestedRaw <= nodeDirectMaxRaw;
  const nodeDirectClaimFeeRaw = nodeDirectRequestedRaw != null && nodeDirectRequestedRaw > 0n
    ? nodeDirectRequestedRaw * 100n / 10_000n : 0n;
  const nodeDirectClaimNetRaw = nodeDirectRequestedRaw != null
    ? nodeDirectRequestedRaw - nodeDirectClaimFeeRaw : 0n;
  const latestNodeDirectWithdrawal = props.nodeWithdrawals.find((item) => item.withdrawal_type === 1);
  const nodeDirectClaimInFlight = Boolean(
    (latestNodeDirectWithdrawal
      && [0, 1, 2, 3, 9].includes(latestNodeDirectWithdrawal.withdrawal_status))
      || (!latestNodeDirectWithdrawal && props.nodeDirectClaimResult
        && [0, 1, 2, 3, 9].includes(props.nodeDirectClaimResult.status))
  );
  const nodeDirectClaimStatus = latestNodeDirectWithdrawal?.withdrawal_status
    ?? props.nodeDirectClaimResult?.status;
  const nodeDirectClaimId = latestNodeDirectWithdrawal?.withdraw_id
    ?? props.nodeDirectClaimResult?.withdrawId;
  const nodeDirectClaimNet = latestNodeDirectWithdrawal?.net_amount
    ?? props.nodeDirectClaimResult?.amounts.net;
  const nodePrincipal = Number(props.node?.principalBalance ?? 0);
  const nodeMaturityAt = props.node?.maturityAt ? new Date(props.node.maturityAt) : null;
  const nodeExitEarly = Boolean(nodeMaturityAt && Date.now() < nodeMaturityAt.getTime());
  const nodeExitFee = nodePrincipal * .01;
  const nodeExitNet = Math.max(0, nodePrincipal - nodeExitFee);
  const recordedNodeExit = props.node?.principalWithdrawal;
  const nodeExitInFlight = Boolean(
    props.nodePrincipalExitResult
      || (recordedNodeExit && [0, 1, 2, 3, 9].includes(recordedNodeExit.status))
  );
  const nodeExitDisplayId = recordedNodeExit?.withdrawId ?? props.nodePrincipalExitResult?.withdrawId;
  const nodeExitDisplayNet = recordedNodeExit?.netAmount ?? props.nodePrincipalExitResult?.amount;
  useEffect(() => {
    const scrollRoot = document.querySelector<HTMLElement>(".sage-scroll");
    const holdRoot = scrollRoot?.querySelector<HTMLElement>(".sage-hold");
    if (!scrollRoot || !holdRoot) return;
    const sections = Array.from(holdRoot.querySelectorAll<HTMLElement>(
      ":scope > .hold-card, :scope > .invite-detected, :scope > .yield-cards"
    ));
    if (!sections.length) return;
    sections.forEach((section) => section.classList.add("hold-section-frame"));
    let frame = 0;
    const activate = (target: HTMLElement) => {
      sections.forEach((section) => section.classList.toggle("is-scroll-active", section === target));
    };
    const updateFromScroll = () => {
      if (scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight < 8) {
        activate(sections[sections.length - 1]!);
        return;
      }
      const viewport = scrollRoot.getBoundingClientRect();
      const focusY = viewport.top + Math.min(viewport.height * .42, 320);
      const visible = sections.filter((section) => {
        const rect = section.getBoundingClientRect();
        return rect.bottom > viewport.top + 72 && rect.top < viewport.bottom - 72;
      });
      const candidates = visible.length ? visible : sections;
      const closest = candidates.reduce((best, section) => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - focusY);
        return distance < best.distance ? { section, distance } : best;
      }, { section: candidates[0]!, distance: Number.POSITIVE_INFINITY });
      activate(closest.section);
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateFromScroll);
    };
    const pointerHandlers = sections.map((section) => {
      const handler = () => activate(section);
      section.addEventListener("pointerdown", handler, { passive: true });
      section.addEventListener("focusin", handler);
      return { section, handler };
    });
    scrollRoot.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    scheduleUpdate();
    return () => {
      cancelAnimationFrame(frame);
      scrollRoot.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      pointerHandlers.forEach(({ section, handler }) => {
        section.removeEventListener("pointerdown", handler);
        section.removeEventListener("focusin", handler);
        section.classList.remove("hold-section-frame", "is-scroll-active");
      });
    };
  }, [props.market, props.referral, props.node]);
  const rank = Math.max(0, props.rankLevel);
  const team = props.market?.earnings.find((item) => item.earning_type === 3);
  const latestRewardDate = props.market?.teamRewards?.[0]?.settlement_date;
  const normalRewardRows = props.market?.teamRewards.filter((item) => item.reward_type === 1) ?? [];
  const peerRewardRows = props.market?.teamRewards.filter((item) => item.reward_type === 2) ?? [];
  // 三项累计一律取生命周期口径：直推与团队分别来自 earnings 的 total_earned，
  // 级差与平级来自 teamRewardTotals。teamRewards 只有最近 30 组（约半个月），
  // 只能用于"最近日结"增量，不能当累计用。
  const normalRewardTotal = Number(props.market?.teamRewardTotals?.normal_team_total ?? 0);
  const peerRewardTotal = Number(props.market?.teamRewardTotals?.peer_total ?? 0);
  const teamRewardTotal = Number(team?.total_earned ?? 0) || normalRewardTotal + peerRewardTotal;
  const teamMemberCount = props.network?.teamCount ?? 0;
  const normalRewardToday = normalRewardRows.filter((item) => item.settlement_date === latestRewardDate).reduce((sum, item) => sum + Number(item.credited_reward), 0);
  const peerRewardToday = peerRewardRows.filter((item) => item.settlement_date === latestRewardDate).reduce((sum, item) => sum + Number(item.credited_reward), 0);
  const latestRewardLabel = latestRewardDate ? String(latestRewardDate).slice(5, 10) : "";
  // 等级进度取真实小区业绩与下一级门槛之比；小区只用于定级，不是任何一项收益的基数。
  const rankThresholds = [10000, 50000, 100000, 500000, 1000000, 5000000, 10000000];
  const smallArea = Number(props.market?.rank?.small_area_performance ?? 0);
  const nextThreshold = rankThresholds[Math.min(rank, rankThresholds.length - 1)] ?? 0;
  const rankProgress = rank >= 7 ? 100 : Math.min(100, nextThreshold ? smallArea / nextThreshold * 100 : 0);
  // 进度为 0 时不留最小色块：未定级还显示一截紫条会被读成"已经在 V1 了"。
  const rankBarWidth = rankProgress <= 0 ? 0 : Math.max(6, rankProgress);
  const selected = props.products.find((item) => item.product_code === props.selectedCode);
  const amount = Number(props.principal || 0);
  const rateMin = Number(selected?.min_daily_rate ?? 0);
  const rateMax = Number(selected?.max_daily_rate ?? 0);
  // 产品上线时三个周期一律以许可区间的最低日收益率作为默认口径；
  // 管理中台后续发布某个结算日的实际收益率后，才切换为该已发布值。
  const latestRate = Number(selected?.published_daily_rate ?? rateMin);
  const days = selected?.lock_days ?? 180;
  const staticBandCode = active?.product_code ?? props.selectedCode;
  const staticBandDefaults: Record<ProductCode, { days: number; min: number; max: number }> = {
    MARKET_7D: { days: 7, min: .005, max: .007 },
    MARKET_90D: { days: 90, min: .007, max: .01 },
    MARKET_180D: { days: 180, min: .01, max: .012 }
  };
  const staticBandProduct = props.products.find((item) => item.product_code === staticBandCode);
  const staticBand = staticBandDefaults[staticBandCode];
  const staticBandDays = staticBandProduct?.lock_days ?? staticBand.days;
  const staticBandMin = Number(staticBandProduct?.min_daily_rate ?? staticBand.min);
  const staticBandMax = Number(staticBandProduct?.max_daily_rate ?? staticBand.max);
  const latestStaticReward = active
    ? props.market?.staticRewards?.find((item) => String(item.position_id) === String(active.position_id))
    : props.market?.staticRewards?.[0];
  const earningCap = amount * 3;
  const usdtValue = amount;
  const usableQuote = isUsdtOnlyParticipationQuote(props.quote, props.selectedCode, props.principal) ? props.quote : undefined;
  const bound = Boolean(props.referral?.relation);
  const nodeHasEnoughUsdt = hasParticipationBalance(props.nodeUsdtBalance);
  const nodeInviteDetected = Boolean(
    props.resolved?.valid && props.resolved.allowedChoices.includes("NODE")
  );
  const nodeInviteReady = props.nodeReferralBound;
  const nodeJoinBlocked = props.authenticated && (
    !props.nodeEligibility?.eligible
    || !nodeInviteReady
    || props.nodeUsdtBalancePending
    || !nodeHasEnoughUsdt
  );
  const nodeJoinLabel = !props.authenticated
    ? h("连接钱包后申请合伙人", "Connect wallet to apply as a partner")
    : !props.nodeEligibility
        ? h("正在核验合伙人参与资格…", "Checking partner eligibility…")
      : props.nodeDepositHash || props.nodeEligibility?.depositPending
        ? h("等待 3 个区块确认…", "Waiting for 3 confirmations…")
        : props.nodeEligibility?.reason === "ORDINARY_IDENTITY_ALREADY_SELECTED"
          ? h("已选择普通参与，不能申请合伙人", "Ordinary identity selected")
          : props.nodeParticipationPending
          ? h("等待钱包确认…", "Waiting for wallet…")
          : !nodeInviteReady
            ? nodeInviteDetected
              ? h("请先确认绑定合伙人推荐人", "Bind the partner referrer first")
              : h("需通过有效合伙人邀请链接进入", "A valid partner invite is required")
            : props.nodeUsdtBalancePending || props.nodeUsdtBalance === undefined
              ? h("正在读取 USDT 余额…", "Reading USDT balance…")
              : !nodeHasEnoughUsdt
                ? h("USDT 余额不足 1,000", "USDT balance below 1,000")
                : h("申请合伙人 · 1,000 USDT", "Apply as partner · 1,000 USDT");
  const earningTitles = props.language === "zh" ? ["", "静态收益到账", "Market 直推收益到账", "团队收益到账", "手续费分红"] : ["", "Static yield credited", "Market direct yield credited", "Team yield credited", "Fee dividend"];
  const history = [
    ...(props.market?.earnings ?? []).filter((item) => item.earning_type !== 5).map((item, index) => ({ id: `e-${item.earning_type}`, type: "in" as const, title: earningTitles[item.earning_type] ?? h("收益到账", "Yield credited"), meta: `${h("收益账本", "Yield ledger")} · #${index + 1}`, amount: Number(item.total_earned), status: h("已完成", "Done") })),
    ...(props.market?.withdrawals ?? []).map((item) => ({ id: `w-${item.withdraw_id}`, type: "out" as const, title: item.withdrawal_type === 2 && Number(item.iage_principal_written_off ?? 0) > 0 ? h("本金退出", "Principal returned") : h("收益提现", "Withdrawn to wallet"), meta: String(item.requested_at ?? ""), amount: -Number(item.wallet_usdt_net_amount ?? item.net_amount), status: props.language === "zh" ? statusText(item.withdrawal_status) : item.withdrawal_status === 4 ? "Done" : item.withdrawal_status === 5 ? "Failed" : "Pending" })),
    ...props.nodeWithdrawals
      .filter((item) => item.withdrawal_type === 1)
      .map((item) => ({ id: `nw-${item.withdraw_id}`, type: "out" as const, title: h("节点直推收益领取", "Node direct yield claim"), meta: String(item.request_time ?? ""), amount: -Number(item.net_amount), status: item.withdrawal_status === 9 ? h("等待流动性", "Awaiting liquidity") : props.language === "zh" ? statusText(item.withdrawal_status) : item.withdrawal_status === 4 ? "Done" : item.withdrawal_status === 5 ? "Failed" : "Pending" }))
  ].filter((item) => props.historyTab === "all" || item.type === props.historyTab);
  return <main className="sage-hold sage-content">
    <section className="hold-card tier-card">
      <header><Medal/><strong>{h("持仓等级与团队收益", "Tier & team yield")}</strong><em>{rank ? `LV.${rank}` : h("未定级", "Unranked")}</em></header>
      <div className="tier-progress"><i><b style={{ width: `${rankBarWidth}%` }}/></i><span>{rank >= 7 ? h("已达最高等级 LV.7", "Top tier LV.7 reached") : h(`小区 $${money(smallArea)} / $${money(nextThreshold)} → LV.${rank + 1}`, `Weak leg $${money(smallArea)} / $${money(nextThreshold)} → LV.${rank + 1}`)}</span></div>
      <div className="tier-ladder">{[1,2,3,4,5,6,7].map((level) => <span key={level}><i className={level <= rank ? "filled" : level === rank + 1 ? "next" : ""}/><b>{level}</b></span>)}</div>
      <div className="tier-values">
        <div><span>{h("团队收益累计", "Team yield to date")}</span><strong>${money(teamRewardTotal)}</strong></div>
        <div><span>{latestRewardLabel ? h(`最近日结 ${latestRewardLabel}`, `Last close ${latestRewardLabel}`) : h("最近日结", "Last close")}</span><strong className="up">+${money(normalRewardToday + peerRewardToday)}</strong></div>
      </div>
      <div className="tier-divider"/>
      <div className="tier-income"><Network/><div><strong>{h(`团队收益（团队总人数：${teamMemberCount}）`, `Team yield (Total members: ${teamMemberCount})`)}</strong></div><div className="tier-income-amount"><b>${money(normalRewardTotal)}</b></div></div>
      <div className="tier-income"><GitBranch/><div><strong>{h("平级/超越收益", "Peer/overtake yield")}</strong></div><div className="tier-income-amount"><b>${money(peerRewardTotal)}</b></div></div>
      <div className="tier-note">
        <span><Info/>{h("小区业绩只用于 V1 - V7 定级", "Small-area performance is used only for V1 - V7 ranking")}</span>
      </div>
    </section>
    {active && <section className="hold-card market-position-card">
      <header><Hash/><strong>{isBreachFrozen ? h("Market 违约冻结仓位", "Breach-frozen Market position") : active.position_status === 1 ? h("Market 有效仓位", "Active Market position") : active.position_status === 2 ? h("Market 收益结算完成仓位", "Market earnings settled") : h("Market 退出中仓位", "Market position exiting")}</strong><span>#{active.contract_position_id ?? active.position_id}</span><em>{active.lock_days}</em></header>
      <div className="market-position-value"><span>{h("参与本金（USDT 计价）", "Principal (USDT value)")}</span><strong>${money(active.principal_usdt_equivalent)}</strong><small>{isBreachFrozen ? h(`收益已于 ${active.earning_stop_date ?? "—"} 停止`, `Yield stopped on ${active.earning_stop_date ?? "—"}`) : <>{h("收益期剩余", "earning days left")} {active.remaining_earning_days} {h("天", "days")}</>}</small></div>
      <div className="market-position-split"><div><span>USDT</span><strong>{money(active.usdt_amount)}</strong></div><b>+</b><div><span>IAGE</span><strong>{money(active.iage_amount)}</strong></div></div>
      <div className="market-position-meta"><span>{h("开始", "Started")} {String(active.started_at).slice(0,10)}</span><span>{h("可正常退出", "Normal exit")} {String(active.maturity_at).slice(0,10)}</span></div>
      <div className="market-position-progress"><div><span>{h("累计静态 + 动态收益", "Static + dynamic earned")}</span><b>${money(activeEarned)} / ${money(activeCap)}</b></div><i><b style={{ width: `${activeProgress}%` }}/></i></div>
      {isForcedExitPending && <div className="market-exit-requested"><Clock3/><div><strong>{h("三倍出局正在链上执行", "The 3× forced exit is being finalized on-chain")}</strong><span>{h("出局确认后保留当前等级，可重新选择 7/90/180 天并达到最低本金后开仓。", "After confirmation, your current tier remains and you may reopen for 7/90/180 days after meeting the minimum principal.")}</span></div></div>}
      {isBreachFrozen && <div className="market-exit-requested"><Clock3/><div><strong>{h("违约已确认，仓位收益已永久停止", "Breach confirmed; position yield has permanently stopped")}</strong><span>{h(`自 ${active.earning_stop_date ?? "申请当日"} 起不再产生新收益；此前已结算收益仍可领取，本金由平台人工核算处理。该申请不可撤销。`, `No new yield accrues from ${active.earning_stop_date ?? "the request date"}. Previously settled yield remains claimable, and principal is handled manually by the platform. This request cannot be cancelled.`)}</span></div></div>}
      {awaitingNormalExit && !isBreachFrozen && <div className="market-exit-requested"><Clock3/><div><strong>{h("收益日结已完成，等待链上可正常退出时间", "Earnings settled; awaiting on-chain normal exit time")}</strong><span>{h("可正常退出时间", "Normal exit time")} {formatShanghaiTimestamp(active.maturity_at, props.language)} · {h("剩余", "Remaining")} {formatPositionCountdown(active.maturity_at, exitCountdownNow, props.language)}</span></div></div>}
      {!isForcedExitPending && !isBreachFrozen && !awaitingNormalExit && active.contract_position_id && (props.principalWithdrawalBlocked || props.exitConfirmId !== active.position_id) && <button className={exitReady ? "market-position-exit" : "market-position-exit is-early"} type="button" disabled={props.exitPending || props.principalWithdrawalBlocked} onClick={() => { if (!props.principalWithdrawalBlocked) props.onRequestExit(active.position_id); }}><LogOut/>{props.principalWithdrawalBlocked ? h("本金提取已限制", "Principal withdrawal restricted") : !exitReady ? h(`申请违约退出（收益永久停止，剩余 ${active.remaining_earning_days} 天到期）`, `Request breach exit (yield permanently stops · ${active.remaining_earning_days} days left)`) : active.identity_type === 2 ? h("到期退出 Market 加仓本金", "Exit matured Market top-up") : h("到期提取本金", "Withdraw matured principal")}</button>}
      {active.identity_type === 2 && <p className="market-position-exit-note"><Info/><span>{active.contract_position_id ? h("退出只处理追加到 Market 的本金；节点质押的 1,000 USDT 由原节点账本处理。", "Only Market top-up principal exits here; the original 1,000 USDT remains in the node ledger.") : h("节点质押的 1,000 USDT 由原节点账本处理。", "The original 1,000 USDT stays in the node ledger.")}</span></p>}
      {!props.principalWithdrawalBlocked && !isForcedExitPending && !isBreachFrozen && !awaitingNormalExit && active.contract_position_id && props.exitConfirmId === active.position_id && <div className="market-exit-review">
        <strong>{!exitReady ? h("确认不可撤销的违约申请？", "Confirm this irreversible breach request?") : active.identity_type === 2 ? h("确认提取到期的 Market 加仓本金？", "Withdraw matured Market top-up principal?") : h("确认提取到期本金？", "Withdraw matured principal?")}</strong>
        <p>{!exitReady ? h("确认后不可撤销，该仓位从今天起永久停止产生静态、直推及团队等新收益；此前已结算收益仍可领取。本操作不会发起链上本金退出，本金由平台人工核算处理。", "Once confirmed, this cannot be cancelled. The position permanently stops generating new static, referral, team, and other yield from today. Previously settled yield remains claimable. No on-chain principal exit is initiated; principal is handled manually by the platform.") : active.identity_type === 2 ? h("仅结算追加到 Market 合约的本金，节点质押的 1,000 USDT 不会被重复退回。", "Only principal added through the Market contract is settled; the node stake is never refunded twice.") : h("仓位已经完成全部收益日结，仅对退回本金收取 1% 平台手续费。", "All yield days have settled; only the 1% platform fee applies to returned principal.")}</p>
        {exitReady && <dl><div><dt>{h("退出手续费 1%", "Exit fee 1%")}</dt><dd>{money(exitUsdtFee)} USDT · {money(exitIageFee)} {h("内部 IAGE", "internal IAGE")}</dd></div>{internalIagePrincipal > 0 && iageRefundPreviewKnown && <div><dt>{writeOffIagePrincipal ? h("内部 IAGE 核销，不再返还", "Internal IAGE written off; not refunded") : h("退回平台内部账本", "Returned to internal ledger")}</dt><dd>{money(exitIageNet)} IAGE</dd></div>}<div><dt>{h("预计到钱包", "Estimated wallet receipt")}</dt><dd>{money(exitUsdtNet)} USDT</dd></div></dl>}
        {!exitReady && <small>{h("系统只记录违约并冻结该仓位后续收益，不调用违约退出合约，也不进入 Treasury 多签流程。", "The system only records the breach and freezes future position yield. It does not call the early-exit contract or enter the Treasury multisig flow.")}</small>}
        {props.exitError && <p className="v4-error">{props.exitError}</p>}
        <div><button type="button" disabled={props.exitPending} onClick={props.onCancelExit}>{h("返回", "Back")}</button><button type="button" disabled={props.exitPending} onClick={() => props.onConfirmExit(active.position_id, active.contract_position_id!, exitPenaltyBps)}>{props.exitPending ? h("正在提交…", "Submitting…") : !exitReady ? h("确认违约并永久停止收益", "Confirm breach and permanently stop yield") : h("确认并提交链上退出", "Confirm on-chain exit")}</button></div>
      </div>}
    </section>}
    {!isTopUp && <section className="hold-card join-card">
      <header><Rocket/><strong>{isExitPending ? h("等待原仓位退出完成", "Awaiting prior position exit") : h("参与入口", "Join a pool")}</strong><span>MIN 300 USDT</span></header>
      <div className="quota">
        <div><span>{h("7 天周期 · 平台每日限 100 个激活名额", "7-day term · 100 activations per day platform-wide")}</span><i><b style={{ width: `${Math.min(100, Number(props.products.find((item) => item.product_code === "MARKET_7D")?.today_position_count ?? 0))}%` }}/></i></div>
        <em>{h("剩余", "left")} {props.products.find((item) => item.product_code === "MARKET_7D")?.remainingToday ?? "—"} / 100</em>
      </div>
      <div className="join-fields">
        <label><span>{h("总参与额（USDT 计价）", "Total (priced in USDT)")}</span><div><b>$</b><input type="number" inputMode="decimal" disabled={isExitPending} value={props.principal} onChange={(event) => props.setPrincipal(event.target.value)}/><em>USDT</em></div></label>
        <div className="join-term-select-field"><span>{h("锁定周期", "Lock term")}</span><div className={termMenuOpen ? "join-select-control is-open" : "join-select-control"} ref={termMenuRef}>
          <button className="join-select-trigger" type="button" disabled={termSelectionLocked} aria-haspopup="listbox" aria-expanded={termMenuOpen} onClick={() => setTermMenuOpen((open) => !open)}><strong>{selected?.lock_days ?? "—"}</strong><ChevronDown/></button>
          {termMenuOpen && <div className="join-select-menu" role="listbox" aria-label={h("选择锁定周期", "Choose lock term")}>{props.products.map((item) => {
            const capacityFull = item.product_code === "MARKET_7D" && item.remainingToday === 0 && !active;
            return <button key={item.product_code} type="button" role="option" aria-selected={props.selectedCode === item.product_code} disabled={capacityFull} onClick={() => { props.setSelectedCode(item.product_code); setTermMenuOpen(false); }}><strong>{item.lock_days}</strong>{capacityFull && <small>{h("今日已满", "Full today")}</small>} {props.selectedCode === item.product_code && <Check/>}</button>;
          })}</div>}
        </div></div>
      </div>
      <div className="pay-box" aria-disabled={isExitPending}>
        <span>{h("参与方式", "Payment")}</span>
        <div className="pay-modes">
          <button type="button" disabled={isExitPending} className="active">100% USDT<small>{h("纯 USDT 参与", "USDT only")}</small></button>
        </div>
        <div className="split-values is-usdt-only"><div><span>USDT</span><strong>{money(usdtValue)}</strong></div></div>
        <p>{h("全部参与本金使用 USDT。", "The full participation principal is paid in USDT.")}</p>
      </div>
      <div className="join-stats"><div><span>{h("当前日收益率", "Current daily rate")}</span><strong>{(latestRate*100).toFixed(1)}%</strong></div><div><span>{h("锁定周期", "Lock term")}</span><strong>{days}</strong></div><div><span>{h("预计收益", "Est. yield")}</span><strong>${money(amount * latestRate * days)}</strong></div></div>
      <div className="join-warning"><AlertTriangle/><span>{isBreachFrozen ? h("该仓位已确认违约并永久停止新收益，申请不可撤销；此前已结算收益仍可领取，本金由平台人工核算。原链上仓位仍有效期间不能加仓或重新开仓。", "This position has an irreversible confirmed breach and permanently earns no new yield. Previously settled yield remains claimable and principal is handled manually. You cannot top up or reopen while the original on-chain position remains active.") : isExitPending ? h("原仓位本金或三倍出局尚未完成链上退出；退出确认前不能加仓或重新开仓。等级保留，但无有效本金期间不产生静态与动态收益。", "The prior principal or 3× forced exit is not yet finalized on-chain. Top-ups and reopening stay disabled until confirmation. The tier is retained, but no static or dynamic yield accrues without active principal.") : !active && props.nodeEligibility?.identityChoice == null ? h("首次确认后将锁定为普通参与身份。本金在所选周期及最后一次日结完成前锁定。", "Your first confirmation selects the ordinary participation identity. Principal remains locked through the selected term and final daily close.") : h("退出完成后可任选 7/90/180 天重新开仓；等级保留，但只有重新投入达到平台最低本金后才恢复静态与动态收益。", "After exit, you may reopen for 7/90/180 days. Your tier is retained, but static and dynamic yield resume only after meeting the platform minimum principal.")}</span></div>
      <div className="join-cap"><RefreshCw/><div><span>{h("任一周期，总收益达到参与本金的 3 倍即强制退出，本金不再退回；身份等级保留，需重新参与才能继续。", "At 3× total yield the position exits and principal is no longer refundable. Your tier stays; a new participation is required to continue.")}</span><small>{h("出局线", "Exit cap")} ${money(earningCap)} · ${money(amount)} × 3</small></div></div>
      {usableQuote && <div className="quote-inline">{h("规则预览", "Rule preview")}: USDT {money(usableQuote.quote.usdtAmount)}</div>}
      <button className="join-submit" disabled={isExitPending || nodeAwaitingInitialMarketSync || amount < 300 || !bound || props.quotePending || props.participationPending} onClick={usableQuote ? props.onParticipate : props.onQuote}>{isExitPending ? h("等待原仓位退出完成", "Awaiting prior position exit") : nodeAwaitingInitialMarketSync ? h("已选择节点质押，等待节点仓位同步", "Node identity selected; awaiting sync") : props.participationPending ? h("等待钱包确认…", "Waiting for your wallet…") : props.quotePending ? h("核验规则中…", "Checking rules…") : !bound ? h("需先绑定推荐人", "Bind a referrer first") : amount < 300 ? h("最少 300 USDT", "Minimum 300 USDT") : usableQuote ? `${h(hasConfirmedMarketExit ? "确认重新开仓" : "确认开仓", hasConfirmedMarketExit ? "Confirm reopening" : "Confirm participation")} · $${money(amount)} · 100% USDT · ${days}d` : h("核验开仓规则", "Review participation")}</button>
      {props.participationError && <p className="v4-error">{props.participationError}</p>}
    </section>}
    <div className="yield-cards"><section className="hold-card"><header><TrendingUp/><strong>{h("个人静态收益", "Static yield")}</strong></header><b>${money(props.staticLedger?.total_earned)}</b><p>{latestStaticReward ? <>{String(latestStaticReward.settlement_date).slice(0,10)} {h("结算", "settled")} <span>+${money(latestStaticReward.credited_reward)}</span> · {pct(latestStaticReward.daily_rate)}</> : h("等待首次日结，实际到账后自动显示", "Awaiting the first daily close; actual credit will appear automatically")}</p><div className="rate-line"><span>{staticBandDays} {h("天周期 · 浮动静态收益率", "day term · floating static rate")}</span><strong>{pct(staticBandMin)}–{pct(staticBandMax)} <small>/ {h("日", "day")}</small></strong><em>{h("每日实际结算率在该收益率区间浮动", "The daily settlement rate floats within this yield-rate range")}</em></div></section><section className="hold-card"><header><Coins/><strong>{h("手续费分红", "Fee dividends")}</strong></header><b>${money(props.node?.feeDividend.totalEarned)}</b><p>{h("上期", "Last cycle")} +${money(props.node?.feeDividend.pendingWeeklyAmount)}</p><div className="fee-note"><Info/>{h("仅限为节点质押的身份参与分红，每周固定发放给参与节点", "Fee dividends are limited to node-staking identities and are paid weekly to participating nodes.")}</div></section></div>
    {/* Partner participation is intentionally closed in this release. Existing
        node identities continue to be rendered by the home/referral surfaces. */}
    {PARTNER_PARTICIPATION_VISIBLE && <section className="hold-card node-card">
      <header><Share2/><strong>{props.node?.nodeStatus ? h("节点质押与质押收益", "Node staking & rewards") : h("合伙人参与与收益", "Partner participation & rewards")}</strong><span>1,000 USDT · 180d</span><em>{props.node?.nodeStatus ? h("已参与", "staking") : h("开放申请", "open")}</em></header>
      <div className="node-values"><div><span>{h("我的节点质押", "My node stake")}</span><strong>{money(props.node?.principalBalance, 0)} USDT</strong></div><div><span>{h("节点直推收益", "Node direct yield")}</span><strong>${money(props.node?.directReferral.totalEarned)}</strong></div><div><span>{h("节点分红收益", "Node dividends")}</span><strong>${money(props.node?.feeDividend.totalEarned)}</strong></div></div>
      <div className="node-bars">{Array.from({ length: 14 }, (_, index) => <i key={index} className={props.node?.nodeStatus && index < 9 ? "active" : ""}/>)}</div>
      {props.node?.nodeStatus ? <>
        <div className="node-meta"><span>{h("节点直推与分红由原节点账本独立核算", "Node direct yield and dividends use the original node ledger")}</span><span>{h("到期", "matures")} {props.node.maturityAt ? String(props.node.maturityAt).slice(0,10) : "—"}</span></div>
        {nodeDirectClaimInFlight ? <div className="node-direct-claim-status"><Clock3/><div><strong>{nodeDirectClaimStatus === 9 ? h("节点直推收益等待兑付流动性", "Node direct yield is awaiting liquidity") : h("节点直推收益领取处理中", "Node direct yield claim is processing")}</strong><span>{h("预计到账", "Expected payout")} {money(nodeDirectClaimNet)} USDT · #{nodeDirectClaimId}</span></div></div> : !nodeDirectClaimReview ? <button className="node-direct-claim" type="button" disabled={nodeDirectMaxRaw <= 0n} onClick={() => { props.onPrepareNodeDirectClaim(); setNodeDirectClaimAmount(nodeDirectMax); setNodeDirectClaimReview(true); }}><Coins/>{nodeDirectMaxRaw > 0n ? h(`领取节点直推收益 · ${money(nodeDirectMax)} USDT`, `Claim node direct yield · ${money(nodeDirectMax)} USDT`) : h("暂无可领取节点直推收益", "No node direct yield available")}</button> : <div className="node-direct-claim-review">
          <strong>{h("确认领取节点直推收益", "Review node direct yield claim")}</strong>
          <p>{h("本次仅领取原节点账本的直推收益；Market 静态与动态收益不受影响。", "This claims direct yield from the original node ledger only. Market static and dynamic yield are unaffected.")}</p>
          <label><span>{h("领取金额（USDT）", "Claim amount (USDT)")}</span><div><input type="text" inputMode="decimal" value={nodeDirectClaimAmount} onChange={(event) => { const value = event.target.value; if (value === "" || /^\d+(?:\.\d{0,18})?$/.test(value)) setNodeDirectClaimAmount(value); }}/><button type="button" onClick={() => setNodeDirectClaimAmount(nodeDirectMax)}>{h("全部", "Max")}</button></div><small>{h("可领取", "Available")} {money(nodeDirectMax)} USDT</small></label>
          <dl><div><dt>{h("领取额度", "Gross claim")}</dt><dd>{money(nodeDirectClaimAmount)} USDT</dd></div><div><dt>{h("手续费 1%", "Fee 1%")}</dt><dd>−{money(formatUnits(nodeDirectClaimFeeRaw, 18))} USDT</dd></div><div><dt>{h("预计到账", "Expected payout")}</dt><dd>{money(formatUnits(nodeDirectClaimNetRaw, 18))} USDT</dd></div></dl>
          {props.nodeDirectClaimError && <p className="v4-error">{props.nodeDirectClaimError}</p>}
          <div className="node-direct-claim-actions"><button type="button" disabled={props.nodeDirectClaimPending} onClick={() => setNodeDirectClaimReview(false)}>{h("取消", "Cancel")}</button><button type="button" disabled={props.nodeDirectClaimPending || !nodeDirectClaimValid} onClick={() => props.onNodeDirectClaim(nodeDirectClaimAmount)}>{props.nodeDirectClaimPending ? h("正在提交…", "Submitting…") : h("确认领取", "Confirm claim")}</button></div>
        </div>}
        {!nodeExitEarly && (nodeExitInFlight ? <div className="node-principal-exit-status"><Clock3/><div><strong>{h("节点本金退出申请处理中", "Node principal exit is processing")}</strong><span>{h("预计到账", "Expected payout")} {money(nodeExitDisplayNet)} USDT · #{nodeExitDisplayId}</span></div></div> : props.principalWithdrawalBlocked || !nodePrincipalExitReview ? <button className="node-principal-exit" type="button" disabled={nodePrincipal <= 0 || props.principalWithdrawalBlocked} onClick={() => { if (!props.principalWithdrawalBlocked) { props.onPrepareNodePrincipalExit(); setNodePrincipalExitReview(true); } }}><LogOut/>{props.principalWithdrawalBlocked ? h("本金提取已限制", "Principal withdrawal restricted") : h("申请退回节点本金", "Withdraw node principal")}</button> : <div className="node-principal-exit-review">
          <strong>{h("确认退出已到期节点本金？", "Withdraw the matured node principal?")}</strong>
          <p>{h("到期后退回原节点本金并收取 1% 手续费；本次不处理任何 Market 追加仓位。", "The matured original node principal is returned with a 1% fee. Market top-ups are not affected.")}</p>
          <dl><div><dt>{h("原节点本金", "Original node principal")}</dt><dd>{money(nodePrincipal)} USDT</dd></div><div><dt>{h("退出手续费 1%", "Exit fee 1%")}</dt><dd>−{money(nodeExitFee)} USDT</dd></div><div><dt>{h("预计到账", "Expected payout")}</dt><dd>{money(nodeExitNet)} USDT</dd></div></dl>
          {props.nodePrincipalExitError && <p className="v4-error">{props.nodePrincipalExitError}</p>}
          <div><button type="button" disabled={props.nodePrincipalExitPending} onClick={() => setNodePrincipalExitReview(false)}>{h("取消", "Cancel")}</button><button type="button" disabled={props.nodePrincipalExitPending} onClick={props.onNodePrincipalExit}>{props.nodePrincipalExitPending ? h("正在提交…", "Submitting…") : h("确认提交退出申请", "Submit exit request")}</button></div>
        </div>)}
      </> : <div className="node-join">
        <div className="node-empty"><CirclePlus/>{h("固定一次性参与 1,000 USDT、锁定 180 天；首次确认后身份不可改为普通参与。生效后同步获得 Market 静态与动态收益身份。", "Join once with a fixed 1,000 USDT for 180 days. The first confirmation is irreversible and also activates the linked Market earning identity.")}</div>
        <div className="node-join-meta"><span>{h("钱包余额", "Wallet balance")} <b>{props.nodeUsdtBalancePending ? h("读取中", "loading") : `${formatUsdtBalance(props.nodeUsdtBalance)} USDT`}</b></span><span>{props.nodeReferralBound ? h("合伙人邀请关系已绑定", "Partner referral bound") : h("需先在上方确认绑定合伙人推荐人", "Bind the partner referrer above first")}</span></div>
        <button className="node-join-submit" type="button" disabled={nodeJoinBlocked || props.nodeParticipationPending || Boolean(props.nodeDepositHash)} onClick={props.onNodeParticipate}>{nodeJoinLabel}</button>
        {props.nodeParticipationError && <p className="v4-error">{props.nodeParticipationError}</p>}
      </div>}
    </section>}
    <section className="hold-card history-card"><header><ReceiptText/><strong>{h("收益到账 & 提现记录", "Credits & withdrawals")}</strong><span>{history.length} {h("条", "records")}</span></header><div className="history-tabs">{(["all","in","out"] as const).map((key,index)=><button key={key} className={props.historyTab===key?"active":""} onClick={()=>props.setHistoryTab(key)}>{(props.language === "zh" ? ["全部","收益到账","提现"] : ["All","Credits","Withdrawals"])[index]}</button>)}</div>{history.length ? history.map((item)=><article key={item.id}><i className={item.type}/><div><strong>{item.title}</strong><small>{item.meta}</small></div><span className={item.amount>=0?"up":""}>{item.amount>=0?"+":"-"}${money(Math.abs(item.amount))}<small>{item.status}</small></span></article>) : <div className="history-empty">{h("完成首次日结后，收益与提现记录会显示在这里。", "Credits and withdrawals appear here after the first daily settlement.")}</div>}</section>
  </main>;
}

export function WithdrawDock({
  language, rewardType, setRewardType, staticAvailable, dynamicAvailable, available,
  pendingClaim, availableSage, availableIage, sagePrice, value, setValue, pending, error, done,
  receivedUsdt, receivedGross, receivedFee, onClaim, onRetryClaim, transferableIage, iageTransfers,
  transferPending, transferError, transferResult, onTransfer
}: {
  language: Language;
  rewardType: RewardTypeName;
  setRewardType: (value: RewardTypeName) => void;
  staticAvailable: string;
  dynamicAvailable: string;
  available: string;
  pendingClaim?: PendingRewardClaim;
  availableSage: string;
  availableIage: string;
  sagePrice: string;
  value: string;
  setValue: (value: string) => void;
  pending: boolean;
  error?: Error | null;
  done: boolean;
  receivedUsdt?: string;
  receivedGross?: string;
  receivedFee?: string;
  onClaim: () => void;
  onRetryClaim: () => void;
  transferableIage: string;
  iageTransfers: MarketMe["iageTransfers"];
  transferPending: boolean;
  transferError?: string;
  transferResult?: IageTransferResult;
  onTransfer: (recipient: string, amount: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [transferExpanded, setTransferExpanded] = useState(false);
  const [transferRecipient, setTransferRecipient] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const maxWithdrawalSelected = useRef(false);
  const claimErrorMessage = friendlyWalletError(marketRewardClaimErrorMessage(error));
  const availableRaw = fixed18Units(available);
  const requestedRaw = parseFixed18Input(value);
  const exceedsAvailable = requestedRaw != null && requestedRaw > availableRaw;
  const valid = requestedRaw != null && requestedRaw > 0n && requestedRaw <= availableRaw;
  const sageBurnRaw = valid && availableRaw > 0n
    ? fixed18Units(availableSage) * requestedRaw / availableRaw : 0n;
  const feeRaw = requestedRaw != null && requestedRaw > 0n
    ? requestedRaw * 100n / 10_000n : 0n;
  const netRaw = requestedRaw != null && requestedRaw > feeRaw
    ? requestedRaw - feeRaw : 0n;
  const requested = requestedRaw == null ? "0" : formatUnits(requestedRaw, 18);
  const fee = formatUnits(feeRaw, 18);
  const net = formatUnits(netRaw, 18);
  const sageBurn = formatUnits(sageBurnRaw, 18);
  const availableIageDisplay = formatUnits(fixed18Units(availableIage), 18);
  const transferableIageRaw = fixed18Units(transferableIage);
  const transferAmountRaw = parseFixed18Input(transferAmount);
  const transferRecipientValid = /^0x[a-fA-F0-9]{40}$/.test(transferRecipient);
  const transferValid = transferRecipientValid && transferAmountRaw != null
    && transferAmountRaw > 0n && transferAmountRaw <= transferableIageRaw;
  const pendingStatus = pendingClaim?.can_retry
    ? textFor(language, "签名已过期或尚未提交", "Signature expired or not submitted")
    : textFor(language, "链上确认中", "Awaiting on-chain confirmation");

  // Mobile wallet browsers can refresh the live SAGE quote between tapping
  // "Max" and tapping submit. Keep an explicit max selection synchronized
  // with the exact current amount so the submit button does not silently
  // become disabled before the wallet request is sent (observed on Vivo).
  useEffect(() => {
    maxWithdrawalSelected.current = false;
  }, [rewardType, pending, done]);
  useEffect(() => {
    const synchronized = synchronizedMaxWithdrawalValue(
      maxWithdrawalSelected.current,
      value,
      available
    );
    if (synchronized !== undefined) setValue(synchronized);
  }, [available, setValue, value]);

  return <footer className={expanded ? "sage-dock withdraw-dock expanded" : "sage-dock withdraw-dock"}>
    <button className="withdraw-toggle" type="button" aria-expanded={expanded}
      aria-label={textFor(language, expanded ? "收起收益兑付" : "展开收益兑付", expanded ? "Collapse redemption" : "Expand redemption")}
      onClick={() => setExpanded((current) => !current)}>
      <div><span>{textFor(language, "收益兑付", "Yield redemption")}</span><strong>{done
        ? textFor(language, "领取已完成", "Withdrawal confirmed") : pendingClaim
        ? textFor(language, `$${money(pendingClaim.expected_usdt_gross)} 待提交`, `$${money(pendingClaim.expected_usdt_gross)} pending`)
        : `$${money(available)}`}</strong></div>
      <ChevronDown />
    </button>
    {expanded && <div className="withdraw-panel">
    <div className="withdraw-label">
      <span>{textFor(language, "提现账本", "Reward ledger")}</span>
      <span>{done ? textFor(language, "领取已完成", "Withdrawal confirmed") : pendingClaim
        ? textFor(language, `待提交 $${money(pendingClaim.expected_usdt_gross)}`, `$${money(pendingClaim.expected_usdt_gross)} pending`)
        : `$${money(available)} USDT`}</span>
    </div>
    <div className="withdraw-ledger-tabs" role="group"
      aria-label={textFor(language, "选择提现账本", "Select reward ledger")}>
      <button type="button" className={rewardType === "STATIC" ? "active" : ""}
        disabled={pending} aria-pressed={rewardType === "STATIC"} onClick={() => setRewardType("STATIC")}>
        <span>{textFor(language, "静态收益", "Static")}</span>
        <strong>{!done && pendingClaim && rewardType === "STATIC"
          ? textFor(language, `待提交 $${money(pendingClaim.expected_usdt_gross)}`, `$${money(pendingClaim.expected_usdt_gross)} pending`)
          : `$${money(staticAvailable)}`}</strong>
      </button>
      <button type="button" className={rewardType === "DYNAMIC" ? "active" : ""}
        disabled={pending} aria-pressed={rewardType === "DYNAMIC"} onClick={() => setRewardType("DYNAMIC")}>
        <span>{textFor(language, "动态收益", "Dynamic")}</span>
        <strong>{!done && pendingClaim && rewardType === "DYNAMIC"
          ? textFor(language, `待提交 $${money(pendingClaim.expected_usdt_gross)}`, `$${money(pendingClaim.expected_usdt_gross)} pending`)
          : `$${money(dynamicAvailable)}`}</strong>
      </button>
    </div>
    {done && <section className="withdraw-done" role="status"><CheckCircle2/><div>
      <strong>{receivedUsdt ? textFor(language, `${money(receivedUsdt)} USDT 已到账`, `${money(receivedUsdt)} USDT received`) : textFor(language, "收益领取交易已确认", "Withdrawal confirmed")}</strong>
      <small>{receivedGross && receivedFee ? textFor(language, `对应 SAGE 已销毁 · 兑付额 ${money(receivedGross)} USDT · 手续费 ${money(receivedFee)} USDT · IAGE 余额不变`, `The corresponding SAGE was burned · gross ${money(receivedGross)} USDT · fee ${money(receivedFee)} USDT · IAGE unchanged`) : textFor(language, "对应 SAGE 已销毁，IAGE 仍保留在平台内部账本。", "The corresponding SAGE was burned; IAGE remains in the on-platform ledger.")}</small>
      <small>{textFor(language, "可切换上方静态或动态账本继续领取，无需退出账户。", "Select Static or Dynamic above to continue claiming without signing out.")}</small>
    </div></section>}
    {!done && pendingClaim && <section className="pending-reward-claim">
      <header><Clock3/><div><strong>{textFor(language,
        `${rewardType === "STATIC" ? "静态" : "动态"}收益有一笔待提交兑付`,
        `A ${rewardType.toLowerCase()} reward claim is pending`)}</strong><small>{pendingStatus} · Claim {short(pendingClaim.claim_id)}</small></div></header>
      <div className="pending-reward-claim-values">
        <span>{textFor(language, "兑付额度", "Gross")}<strong>{money(pendingClaim.expected_usdt_gross, 4)} USDT</strong></span>
        <span>{textFor(language, "手续费", "Fee")}<strong>{money(pendingClaim.expected_usdt_fee, 4)} USDT</strong></span>
        <span>{textFor(language, "预计到账", "Net payout")}<strong>{money(pendingClaim.expected_usdt_net, 4)} USDT</strong></span>
      </div>
      <p>{textFor(language,
        "重新签名会沿用同一笔 Claim，不会重复扣减收益；钱包最终金额按本次实时 SAGE 报价更新。",
        "Re-signing reuses the same Claim and cannot reserve the reward twice. The wallet receives a refreshed live SAGE quote.")}</p>
      <button type="button" disabled={pending || !pendingClaim.can_retry} onClick={onRetryClaim}>
        <RefreshCw/>{pending
          ? textFor(language, "正在生成新签名并唤起钱包…", "Refreshing the signature and opening the wallet…")
          : pendingClaim.can_retry
            ? textFor(language, "重新签名并提交", "Re-sign and submit")
            : textFor(language, "等待链上确认", "Await on-chain confirmation")}
      </button>
    </section>}
    {!done && !pendingClaim && <>
    <div className={exceedsAvailable ? "withdraw-input error" : "withdraw-input"}><b>$</b><input type="number" inputMode="decimal" step="0.000000000000000001" disabled={pending} value={value} placeholder={money(available)} onChange={(event) => { const next = event.target.value; if (next === "" || /^\d+(?:\.\d{0,18})?$/.test(next)) { maxWithdrawalSelected.current = false; setValue(next); } }}/><button type="button" disabled={pending} onClick={() => { maxWithdrawalSelected.current = true; setValue(available); }}>{textFor(language, "全部", "Max")}</button></div>
    <div className="withdraw-preview">
      <span>{textFor(language, "预计销毁", "Estimated burn")}<strong>{money(sageBurn, 6)} SAGE</strong></span>
      <span>{textFor(language, "平台内 IAGE 余额", "On-platform IAGE")}<strong>{money(availableIageDisplay, 6)} IAGE</strong></span>
      <span>{textFor(language, "USDT 手续费", "USDT fee")}<strong>{money(fee, 4)} · 1%</strong></span>
      <span>{textFor(language, "预计到账", "Estimated payout")}<strong>{money(net, 4)} USDT</strong></span>
    </div>
    </>}
    <section className={transferExpanded ? "iage-transfer is-expanded" : "iage-transfer"}>
      <button className="iage-transfer-toggle" type="button"
        aria-expanded={transferExpanded}
        onClick={() => setTransferExpanded((current) => !current)}>
        <span><Send/>{textFor(language, "转账平台内 IAGE 权益", "Transfer on-platform IAGE rights")}</span>
        <strong>{tokenAmount(transferableIage)} IAGE</strong>
        <ChevronDown/>
      </button>
      {transferExpanded && <div className="iage-transfer-panel">
        <p><Info/>{textFor(language,
          "仅转移平台内 IAGE 权益，不发生链上代币转账。收款钱包须先确认绑定推荐人，钱包确认转账后不可撤销。",
          "Transfers on-platform IAGE rights only, with no ERC-20 transfer. The recipient must first confirm a referrer binding, and a confirmed transfer is irreversible.")}</p>
        <label><span>{textFor(language, "收款钱包", "Recipient wallet")}</span>
          <input type="text" inputMode="text" autoCapitalize="none" autoCorrect="off"
            placeholder="0x…" value={transferRecipient}
            onChange={(event) => setTransferRecipient(event.target.value.trim())}/>
        </label>
        <label><span>{textFor(language, "转账数量", "Transfer amount")}</span>
          <div><input type="text" inputMode="decimal" placeholder="0.000000000000000001"
            value={transferAmount}
            onChange={(event) => {
              const next = event.target.value;
              if (next === "" || /^\d+(?:\.\d{0,18})?$/.test(next)) setTransferAmount(next);
            }}/><button type="button" onClick={() => setTransferAmount(transferableIage)}>{textFor(language, "全部", "Max")}</button></div>
          <small>{textFor(language, "可转账", "Transferable")} {tokenAmount(transferableIage)} IAGE</small>
        </label>
        {transferAmountRaw != null && transferAmountRaw > transferableIageRaw
          && <p className="v4-error">{textFor(language, "转账数量超过可用 IAGE 权益", "Amount exceeds transferable IAGE rights")}</p>}
        {transferError && <p className="v4-error">{transferError}</p>}
        {transferResult && <p className="iage-transfer-success"><CheckCircle2/>{textFor(language,
          `${tokenAmount(transferResult.iageAmount)} IAGE 已转给 ${short(transferResult.recipient)}`,
          `${tokenAmount(transferResult.iageAmount)} IAGE transferred to ${short(transferResult.recipient)}`)}</p>}
        <button className="iage-transfer-submit" type="button" disabled={!transferValid || transferPending}
          onClick={() => onTransfer(transferRecipient, transferAmount)}>
          {transferPending ? textFor(language, "等待钱包签名确认…", "Waiting for wallet signature…")
            : textFor(language, "签名确认转账", "Sign and confirm transfer")}
        </button>
        {iageTransfers.length > 0 && <div className="iage-transfer-history">
          <header>{textFor(language, "最近转账", "Recent transfers")}</header>
          {iageTransfers.slice(0, 5).map((item) => {
            const counterpart = item.direction === "SENT" ? item.recipient_address : item.sender_address;
            return <article key={item.transfer_id}>
              <span>{item.direction === "SENT" ? textFor(language, "转出", "Sent") : textFor(language, "转入", "Received")} · {short(counterpart)}</span>
              <strong className={item.direction === "RECEIVED" ? "is-received" : ""}>{item.direction === "SENT" ? "−" : "+"}{tokenAmount(item.iage_amount)} IAGE</strong>
              <small>{formatShanghaiTimestamp(item.created_at, language)}</small>
            </article>;
          })}
        </div>}
      </div>}
    </section>
    {!done && !pendingClaim && <><div className="withdraw-net"><span>{exceedsAvailable ? textFor(language, "SAGE 实时估值已不足，请降低金额", "Live SAGE value is insufficient; lower the amount") : `${textFor(language, "本次兑付额度", "Redemption amount")} ${money(requested)} USDT`}</span><strong>{textFor(language, `SAGE @ ${money(sagePrice, 4)} · 实时兑付`, `SAGE @ ${money(sagePrice, 4)} · live redemption`)}</strong></div>
    <button className="withdraw-submit" disabled={!valid || pending} onClick={onClaim}>{pending ? textFor(language, "锁定 SAGE 实时价格并等待钱包…", "Locking the live SAGE quote…") : `${textFor(language, "销毁 SAGE 并兑付 USDT", "Burn SAGE & redeem USDT")} $${money(requested)}`}</button></>}
    {claimErrorMessage && <p className="v4-error">{claimErrorMessage}</p>}
    </div>}
  </footer>;
}

function WalletSheet({ language, connected, authenticated, address, connecting, disconnecting,
  usdtBalance, sageBalance, syncingAssets, assetsSynced, assetSyncError,
  onSyncAssets, onClose, onConnect, onDisconnect }: {
  language: Language; connected: boolean; authenticated: boolean; address?: string;
  connecting: boolean; disconnecting: boolean; usdtBalance?: bigint; sageBalance?: bigint;
  syncingAssets: boolean; assetsSynced: boolean; assetSyncError?: string;
  onSyncAssets: () => void; onClose: () => void; onConnect: () => void; onDisconnect: () => void;
}) {
  const title = authenticated ? textFor(language, "账户已登录", "Account signed in") : connected ? textFor(language, "完成签名登录", "Complete sign-in") : textFor(language, "连接钱包", "Connect a wallet");
  return <><button className="wallet-backdrop" aria-label={textFor(language, "关闭钱包面板", "Close wallet panel")} onClick={onClose}/><section className="wallet-sheet" aria-modal="true" role="dialog" aria-label={title}><i/><button className="wallet-sheet-close" type="button" aria-label={textFor(language, "关闭", "Close")} onClick={onClose}><X/></button><h2>{title}</h2><p>{textFor(language, "本平台仅支持 TokenPocket。签名在钱包内完成，Sage 不接触私钥。IAGE 仅为平台内部权益，不进入钱包。", "TokenPocket only. Signing happens inside your wallet and Sage never sees your keys. IAGE is an internal platform right and never enters the wallet.")}</p>{authenticated ? <><div className="wallet-option wallet-account"><span>TP</span><div><strong>{textFor(language, "TokenPocket 已登录", "TokenPocket signed in")}</strong><small>{address ? short(address) : textFor(language, "当前钱包", "Current wallet")}</small></div></div>{isLocal && <div className="wallet-local-assets"><header><strong>{textFor(language, "当前测试合约余额", "Current test-contract balances")}</strong><small>{textFor(language, "以此处链上余额为准", "Authoritative on-chain balances")}</small></header><div><span>lUSDT <small>{short(usdtAddress)}</small></span><b>{formatUsdtBalance(usdtBalance)}</b></div><div><span>lSAGE <small>{sageAddress ? short(sageAddress) : "—"}</small></span><b>{formatUsdtBalance(sageBalance)}</b></div><button type="button" disabled={syncingAssets} onClick={onSyncAssets}><RefreshCw/>{syncingAssets ? textFor(language, "等待 TP 确认…", "Confirm in TP…") : textFor(language, "同步当前测试资产到 TP", "Sync current test assets to TP")}</button>{assetsSynced && <p className="wallet-sync-ok"><Check/>{textFor(language, "已提交到 TokenPocket，请隐藏旧合约资产", "Added to TokenPocket; hide the old contract assets")}</p>}{assetSyncError && <p className="v4-error">{assetSyncError}</p>}</div>}<button className="wallet-disconnect" type="button" disabled={disconnecting} onClick={onDisconnect}><LogOut/>{disconnecting ? textFor(language, "正在退出…", "Signing out…") : textFor(language, "退出登录", "Sign out")}</button></> : <button className="wallet-option active" type="button" disabled={connecting} onClick={onConnect}><span>TP</span><div><strong>{connected ? textFor(language, "签名登录", "Sign in") : "TokenPocket"}</strong><small>{connecting ? textFor(language, "等待钱包确认…", "Waiting for your wallet…") : connected ? textFor(language, "点击后在钱包内确认签名", "Confirm the signature in your wallet") : textFor(language, "连接 TokenPocket 钱包", "Connect your TokenPocket wallet")}</small></div></button>}</section></>;
}
